import { ActivityList, HintTip, Icon, OinkIcon } from "../components/Common.jsx";
import { containsRudeWord } from "../lib/rude-words.js";
import { actingAdult, actingAdultHere} from "../lib/adults.js";
import { coachEnabled } from "../lib/coachEnabled.js";
import { DEFAULT_COACH_TEST_SIZE, DEFAULT_PASS_THRESHOLD, VOCAB_TEST_SIZE, uid } from "../lib/constants.js";
import { adaptiveSentence, adaptiveSummary, gameState as adaptiveGameState } from "../lib/adaptive.js";
import { ageBand, track } from "../lib/metrics.js";
import { childOinks, ensureCosmetics, recordOinkEarn } from "../lib/oinks.js";
import { logSetting, todayStr, weekStartOf } from "../lib/utils.js";
import { getDeviceId, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode} from "../lib/api.js";
import { SPELLING_AUTO_DEFAULT_BATCH, SPELLING_AUTO_MAX_BATCH, autoSpellingBatch, effectiveSpellingListOrder, effectiveSpellingLists, resolveSpellingList, spellingListItems } from "../lib/spelling.js";
import { MATHS_WORKOUT_SESSION_SIZE, TT_SESSION_SIZE, TT_YEAR_PRESETS, effectiveMathsBandOrder, effectiveMathsBands, mathsBandTypeIds, resolveMathsBand } from "../lib/maths.js";
import { yearBadge } from "../lib/ladder.js";
import { effectiveStarterVocabList, effectiveVocabCategories, effectiveVocabCategoryOrder, resolveVocabCategory } from "../lib/vocab.js";
import { effectiveAge } from "../lib/age.js";
import { arcadeEnabledFor, arcadeWarmupModeFor } from "../lib/snackCatch.js";
import { t as ct } from "../lib/content.js";
import { RecentTestsCard } from "./TestHistory.jsx";
import { PhotoCapture, warmReader } from "./PhotoCapture.jsx";

// LB-343 — who is doing this; see the twin in SettingsTab.jsx for why it reads the code
// rather than taking it as a prop.
const actor = (s) => actingAdultHere(s, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode());
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// Difficulty Ladder labels (docs/DIFFICULTY_LADDER_SCOPE.md). Once a resolved band/list
// carries a school-year claim, "Auto" says WHAT it resolved to and why — the exact
// legibility ask behind "fun names with year label". Until the ladder import, no entry
// has years and this renders the pre-ladder "Auto (age X)" unchanged. Parent-facing
// only; kid surfaces never show the year badge.
// The "auto" option is the ONE choice that lets the engine move a child. With Adaptive on it
// says so — "Adaptive: Climber, Year 2 (starting point)" (Alan, 12 Sep: "none of the drop
// downs contain the new Adaptive options"); with Adaptive off it is the free "By age" lookup.
function ladderAutoLabel(resolvedEntry, age, adaptiveOn = false) {
  const badge = resolvedEntry && resolvedEntry.years && resolvedEntry.years.length ? yearBadge(resolvedEntry.years) : "";
  if (adaptiveOn) {
    if (badge) return ct("learnearn.ladder.adaptive_suggested", "Learning Coach: {name}, {year} (starting point)", { name: resolvedEntry.label, year: badge });
    return ct("learnearn.ladder.adaptive_age", "Learning Coach (age {age})", { age });
  }
  if (badge) return ct("learnearn.ladder.auto_suggested", "By age: {name}, {year} (suggested)", { name: resolvedEntry.label, year: badge });
  return ct("learnearn.ladder.auto_age", "By age (age {age})", { age });
}
// Adaptive status line under a game's list picker (docs/ADAPTIVE_LE_SCOPE.md §5, §8).
// Premium-tagged, "free during beta"; totals only, never word lists; a pin chip.
function AdaptiveRow({ game, adaptiveOn, manual, state, bandLabel, topicsInBand, onPin }) {
  if (!adaptiveOn) return null;
  if (manual) return <p className="text-xs text-slate-500 leading-relaxed px-1">{ct("adaptive.row.manual", "Learning Coach is standing back while you set this one. Use Hand it back above, or choose Learning Coach in the list, to let it move again.")}</p>;
  const sum = adaptiveSummary(state, topicsInBand);
  return (
    <div className="px-1" data-testid={`adaptive-row-${game}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="font-bold text-slate-800">{ct("adaptive.row.title", "Learning Coach")}</span>
        <span className="text-[10px] font-extrabold uppercase tracking-wide bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">{ct("adaptive.row.premium", "Premium")}</span>
        <span className="text-slate-500">{ct("adaptive.row.beta", "free in the Preview")}</span>
        {onPin && (sum.pinned ? <button type="button" className="ml-auto text-[11px] font-bold underline" onClick={() => onPin(false)}>{ct("adaptive.row.unpin", "Let it move again")}</button>
          : <button type="button" className="ml-auto text-[11px] font-bold underline" onClick={() => onPin(true)} data-testid={`adaptive-pin-${game}`}>{ct("adaptive.row.pin", "Keep at {band}", { band: bandLabel })}</button>)}
      </div>
      <p className="text-xs text-slate-500 leading-relaxed mt-0.5">
        {sum.movedDownAt && onPin && (!sum.movedUpAt || sum.movedDownAt > sum.movedUpAt) ? ct("adaptive.row.band_moved_down", "{band} (moved down {date})", { band: bandLabel, date: sum.movedDownAt })
          : sum.movedUpAt && onPin ? ct("adaptive.row.band_moved", "{band} (moved up {date})", { band: bandLabel, date: sum.movedUpAt }) : bandLabel}
        {" · "}{ct("adaptive.row.totals", "{m} of {n} topics mastered · working at grade {g} of 5", { m: sum.topicsMastered, n: sum.topicsInBand, g: sum.target })}
        {sum.pinned ? " · " + ct("adaptive.row.pinned", "pinned") : ""}
      </p>
    </div>
  );
}
function ladderOptionLabel(entry) {
  const badge = entry && entry.years && entry.years.length ? yearBadge(entry.years) : "";
  return badge ? `${entry.label} \u00b7 ${badge}` : entry.label;
}

// 2026-08 redesign ("Option B", Alan sign-off): the page is now three subject accordions —
// English, Maths, Just for Fun — each expanding to a segmented tab bar where EVERYTHING
// about one game lives in that game's tab (its show/hide toggle, reward amounts, test setup,
// and word/table management as sections down one pane), plus a subject tab for the shared
// weekly payout limit and a plain-words recap of what the subject pays. This replaced the
// old single-scroll layout whose reward cards (top) and Test Management (bottom) were linked
// by an invisible selected-game state, with Just for Fun wedged between them and Oinks
// orphaned at the very bottom.
//
// Save model ("(a) steppers with autosave", Alan): the Edit/Cancel/Save forms are gone.
// Every setting is an always-visible live row — Stepper for whole numbers (minus / typable
// value / plus, committing on tap, blur, or Enter), native selects for lists — writing to
// state immediately, exactly like the toggles always have. Cross-field rules that used to
// hard-block a Save (100% reward >= pass reward) are advisory toasts now, since with
// independent autosaving controls a parent must be able to move one value through a
// temporarily "wrong" state to reach the pair they want.
//
// Open/collapse model: one section open at a time; all collapsed by default in Parent Mode
// (each header carries a live status summary so the collapsed page reads as a dashboard);
// the Wizard passes defaultOpen="english" so onboarding doesn't greet a parent with three
// shut doors.

// The wizard's guided sequence (Alan, 30 Aug 2026). In Parent Mode this page is a
// dashboard — three collapsed accordions a parent dips into when they want something. In
// SETUP it was the same dashboard, which is the wrong shape entirely: a parent who has
// never seen the app is handed three shut doors, a tab bar, and a word list, and told
// nothing about what they are supposed to DO. So the wizard drives one pane at a time,
// in the order Alan asked for — spelling, vocabulary, English reward, times tables, maths
// workout, maths reward — with the section header, the accordion chrome and the tab bar
// all gone, and a plain instruction at the top of each.
//
// `todo` is the point of the whole thing: every screen says what this step is for and
// that there is nothing they MUST change, because there genuinely isn't — every game
// ships on with a starter list already serving.
// One line, three places (Alan, 30 Aug 2026): above the spelling word list, the vocabulary
// word list and the maths difficulty band. All three are "we already picked this for their
// school year" controls, and without saying so the picker reads as a question a parent is
// obliged to answer. Naming the recommendation is what makes leaving it alone a decision.
export const LEVEL_LEAD = "Accept our recommendation, or change the level.";

export const LE_GUIDE_STEPS = [
  { id: "spelling", section: "english", tab: "spelling", label: "Spelling",
    heading: "Spelling test", todo: "Set what a pass and full marks pay, and which words they practise. The list below is already chosen for their school year — leave it be, or swap it for your own." },
  { id: "vocab", section: "english", tab: "vocab", label: "Vocabulary",
    heading: "Vocabulary test", todo: "Same idea for vocabulary: how long the test is, what it pays, and which word list feeds it. A starter list is already loaded." },
  { id: "english", section: "english", tab: "english", label: "English reward",
    heading: "How often English pays", todo: "How many times a week each English test can earn — after that they can still play, it just stops paying." },
  { id: "ttstreaks", section: "maths", tab: "ttstreaks", label: "Times tables",
    heading: "Times tables streaks", todo: "How long a streak earns a reward, what it pays, and which tables are in play. The year presets set the tables in one tap." },
  { id: "mathsworkout", section: "maths", tab: "mathsworkout", label: "Maths workout",
    heading: "Maths workout", todo: "Test length, pass mark, what it pays, and how hard the questions are. Difficulty follows their school year unless you pick a band yourself." },
  { id: "maths", section: "maths", tab: "maths", label: "Maths reward",
    heading: "How often maths pays", todo: "The weekly limit shared by both maths games. Last one — you're done after this." },
];

// What the parent is actually being asked to accept (Alan, 30 Aug 2026).
//
// The wizard now opens Learn & Earn with a summary and two ways out: take the defaults, or
// set them up. A bare "accept defaults" would be asking someone to agree to something they
// cannot see — so this reads the SAME resolved values the games will serve (the ladder
// rung, the batch size, the real reward amounts), never a restatement of what they usually
// are. If a parent has already changed something, the summary says so, because it is
// reading the setting rather than the default.
//
// Lives here rather than in Wizard.jsx because every resolver it needs is already imported
// here, and a second copy of "which list does auto mean for this child" is exactly the kind
// of duplicate that drifts.
export function learnEarnSummary(child, state) {
  const lc = (state.learningContent || {})[child.id] || {};
  const rows = [];
  const on = (id) => lc[`${id}Enabled`] !== false;

  if (on("spelling")) {
    const listKey = resolveSpellingList(child, (lc.spellingAuto || {}).list);
    const batch = (lc.spellingAuto || {}).batchSize || SPELLING_AUTO_DEFAULT_BATCH;
    const list = listKey === "off" ? "Your own words" : ladderOptionLabel(effectiveSpellingLists()[listKey]);
    // LB-284: "N words a week" describes the manual auto-refresher. A household on Coach for
    // spelling (the default from LB-269 step 3) draws coachTestSize per TEST instead, so quoting
    // the weekly batch there is a number that does not apply to them.
    const onCoach = coachEnabled(state.settings) && (lc.spellingAuto || {}).list !== "off" && (!(lc.spellingAuto || {}).list || (lc.spellingAuto || {}).list === "auto");
    const coachSize = lc.coachTestSize || DEFAULT_COACH_TEST_SIZE;
    rows.push({
      id: "spelling", icon: "ic-spelling", label: "Spelling",
      detail: listKey === "off" ? list
        : onCoach ? `Learning Coach · ${coachSize} word${coachSize === 1 ? "" : "s"} a test`
        : `${list} · ${batch} word${batch === 1 ? "" : "s"} a week`,
      // LB-283: spelling was the only game showing a single figure while vocab and times
      // tables showed "3–5 tok". It HAS a pass tier — spellingPassReward is seeded, repaired
      // and paid by the test — it just was not on the review screen, which is the ticket's
      // own title. Same shape as its siblings now.
      pays: `${lc.spellingPassReward ?? 3}–${lc.spellingReward ?? 5} tok`,
    });
  }
  if (on("vocab")) {
    const cat = resolveVocabCategory(child, lc.vocabCategory);
    const label = cat === "custom" ? "Your own words" : effectiveVocabCategories()[cat].label;
    const size = lc.vocabTestSize ?? VOCAB_TEST_SIZE;
    rows.push({
      id: "vocab", icon: "ic-spelling", label: "Vocabulary",
      detail: `${label} · ${size} question${size === 1 ? "" : "s"}`,
      pays: `${lc.vocabPassReward ?? 3}–${lc.vocabFullReward ?? 5} tok`,
    });
  }
  if (on("ttstreaks")) {
    const tables = lc.ttTables && lc.ttTables.length ? lc.ttTables : [2, 3, 4, 5, 10];
    const preset = TT_YEAR_PRESETS.find((pr) => pr.tables.length === tables.length && pr.tables.every((t) => tables.includes(t)));
    const streak = lc.ttStreakPassCount ?? Math.round((lc.ttSessionSize ?? TT_SESSION_SIZE) * 0.8);
    rows.push({
      id: "ttstreaks", icon: "ic-maths", label: "Times tables",
      detail: `${preset ? `Year ${preset.id.slice(1)} tables` : `${tables.length} table${tables.length === 1 ? "" : "s"}`} · streak of ${streak}`,
      pays: `${lc.ttStreakPassReward ?? 3}–${lc.ttStreakFullReward ?? 5} tok`,
    });
  }
  if (on("mathsworkout")) {
    const band = effectiveMathsBands()[resolveMathsBand(child, lc.mathsBand)];
    const size = lc.mathsTestSize ?? MATHS_WORKOUT_SESSION_SIZE;
    rows.push({
      id: "mathsworkout", icon: "ic-maths", label: "Maths workout",
      detail: `${ladderOptionLabel(band)} · ${size} question${size === 1 ? "" : "s"}`,
      pays: `${lc.mathsPassReward ?? 3}–${lc.mathsFullReward ?? 5} tok`,
    });
  }
  return {
    rows,
    english: lc.learnEarnPayoutsPerWeek ?? 1,
    maths: lc.mathsPayoutsPerWeek ?? 1,
  };
}

// A closed-by-default disclosure, used in guided mode to keep word management OFF the
// screen (Alan: "they don't need the full word list open by default"). The vocabulary
// bank in particular is a search box, 26 letter buttons and a paged list of 300+ words —
// legitimate on the Parent Mode page, overwhelming as the second thing a new parent sees.
// Everything inside still exists and still works; it just isn't the default view.
function GuidedDisclosure({ label, hint, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 pt-2.5 border-t border-slate-100">
      <button type="button" data-testid="lea-guided-disclosure" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left">
        <span className="min-w-0">
          <span className="block text-xs font-medium text-slate-600">{label}</span>
          {hint && <span className="block text-xs text-slate-400 font-medium">{hint}</span>}
        </span>
        <span className="text-xs font-bold text-teal-600 flex-shrink-0">{open ? ct("learnearn.guide.hide", "Hide") : ct("learnearn.guide.show", "See more")}</span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

// Word management is the bulk of both English panes, and none of it is something a parent
// has to do at setup: spelling auto-serves a curriculum batch every Monday and vocabulary
// loads a starter list, so the correct answer to both screens during onboarding is "leave
// it alone". In Parent Mode it renders exactly as it always has; in the wizard it goes
// behind a closed disclosure so the settings that DO want a decision are what's on screen.
function WordManager({ guided, label, hint, children }) {
  if (!guided) return children;
  return <GuidedDisclosure label={label} hint={hint}>{children}</GuidedDisclosure>;
}

const ENGLISH_GAMES = [
  { id: "spelling", label: "Spelling" },
  { id: "vocab", label: "Vocabulary" },
];
const MATHS_GAMES = [
  { id: "ttstreaks", label: "TT streaks" },
  { id: "mathsworkout", label: "Maths workout" },
];

// Small iOS-style switch used to show/hide a game from the kid-side menu. Green = visible,
// grey = hidden — kept visually distinct from a card's own selected/active state so a parent
// can't confuse "this pane is open" with "this game is visible to my kid".
function ToggleSwitch({ on, onClick, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onClick}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

// The autosaving number control: minus / typable value / plus. Taps commit immediately
// (clamped to [min, max]); typing commits on blur or Enter, reverting to the last good value
// on nonsense — so Playwright can fill() + press Enter and thumbs can tap, with one control.
// The draft re-syncs whenever the committed value changes from outside (child switch,
// cross-field derivation like the TT streak target).
export function Stepper({ value, min, max, onCommit, label, testId }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  function commit(n) {
    const clamped = Math.max(min, Math.min(max, n));
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  }
  return (
    <span className="inline-flex items-center gap-1.5 flex-shrink-0">
      <button type="button" aria-label={`Decrease ${label}`} disabled={value <= min}
        className={`w-7 h-7 rounded-lg border text-base font-bold leading-none flex items-center justify-center ${value <= min ? "border-slate-100 text-slate-200" : "border-slate-200 text-slate-500"}`}
        onClick={() => commit(value - 1)}>−</button>
      <input aria-label={label} data-testid={testId} inputMode="numeric"
        className="w-10 text-center border border-slate-200 rounded-lg py-1 text-xs font-bold text-slate-700"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
        onBlur={() => { const n = parseInt(draft, 10); if (Number.isNaN(n)) { setDraft(String(value)); return; } commit(n); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
      <button type="button" aria-label={`Increase ${label}`} disabled={value >= max}
        className={`w-7 h-7 rounded-lg border text-base font-bold leading-none flex items-center justify-center ${value >= max ? "border-slate-100 text-slate-200" : "border-slate-200 text-slate-500"}`}
        onClick={() => commit(value + 1)}>+</button>
    </span>
  );
}

// One settings row: label (with optional hint line) left, control right. A div, not a
// <label> — controls include buttons, and a wrapping label would re-fire their clicks.
// ---- LB-19 Zip C: the Manual page re-cut (Alan, drawings v2 screens 3 + 3b, signed off)
// ---------------------------------------------------------------------------------------
// The complaint was never the controls, it was the shape: one long accordion per SUBJECT
// AREA (English/Maths) with tabs inside, every setting and every explanation on screen at
// once. The re-cut is a card per subject with three rows — List, Test, Reward — each
// opening a small page holding ONLY that group, with the long explanations behind ⓘ.
//
// Crucially the controls themselves do not move. Each subject's body is already written as
// a flat list of children partitioned by <GroupLabel> ("Words"/"Difficulty band"/"Tables in
// play", then "Test", then "Reward"), so the sub-page renders the SAME children filtered to
// one group. No markup was cut and pasted between files or reindented — the thing that has
// bitten this workstream before.
const GROUP_OF_LABEL = { "Words": "list", "Tables in play": "list", "Difficulty band": "list", "Custom words": "custom", "Test": "test", "Reward": "reward", "English rewards": "reward", "Maths rewards": "reward" };
export const MANUAL_SUBJECTS = [
  { id: "spelling", label: "Spelling" },
  { id: "vocab", label: "Vocabulary" },
  { id: "mathsworkout", label: "Maths" },
  { id: "ttstreaks", label: "Times tables" },
];
// Which groups each subject actually has, matching the <GroupLabel>s in its body below —
// spelling has no separate Test group (its test settings sit with the words), and the
// cross-subject payout summaries ("What English pays") are folded into the Reward pages.
// scripts/check-kid-sheet.mjs walks every row of every card and fails if one opens an empty
// page, so this map cannot silently drift from the markup.
export const MANUAL_GROUP_MAP = {
  spelling: ["list", "test", "reward"],
  vocab: ["list", "test", "reward"],
  mathsworkout: ["list", "test", "reward"],
  ttstreaks: ["list", "test", "reward"],
};
export const ENGLISH_SUBJECTS = ["spelling", "vocab", "english"];
export const MATHS_SUBJECTS = ["mathsworkout", "ttstreaks", "maths"];
export const MANUAL_GROUPS = [
  { id: "list", label: "List" },
  { id: "custom", label: "Custom words" },
  { id: "test", label: "Test" },
  { id: "reward", label: "Reward" },
];

// Everything before the first GroupLabel (the game on/off row) is "top" — it belongs on the
// subject card itself, where a parent can reach it without opening anything.
// The GroupLabels are not always direct children — spelling's sit inside the card container
// that carries the spelling-auto-card testid. So this walks the tree in document order,
// carrying the "current group" across nesting, and rebuilds any container that holds
// GroupLabels with only the matching children (keeping the container's own testid and
// classes intact). Anything with no GroupLabels inside it is a leaf and belongs to whatever
// group was last opened above it.
function containsGroupLabel(node) {
  if (!node || !node.props) return false;
  let found = false;
  React.Children.forEach(node.props.children, (kid) => {
    if (found || !kid || typeof kid !== "object") return;
    if (kid.type === GroupLabel) found = true;
    else if (containsGroupLabel(kid)) found = true;
  });
  return found;
}

function partitionByGroup(node, group) {
  // Anything above the first GroupLabel (the game on/off row, the Adaptive premium note)
  // belongs with List — it is the "is this game on, and which words" page. Without this the
  // leading content has no home at all, which is exactly what shipped in the first cut of
  // the re-cut and what CI caught.
  const matches = (c) => c === group || (group === "list" && c === "top");
  let current = "top";
  const walk = (children) => {
    const out = [];
    for (const kid of React.Children.toArray(children)) {
      if (kid && kid.type === GroupLabel) {
        const text = React.Children.toArray(kid.props.children).filter((c) => typeof c === "string").join("");
        current = GROUP_OF_LABEL[text.trim()] || current;
        if (matches(current)) out.push(kid);
        continue;
      }
      if (kid && typeof kid === "object" && containsGroupLabel(kid)) {
        const inner = walk(kid.props.children);
        if (inner.length) out.push(React.cloneElement(kid, {}, inner));
        continue;
      }
      if (matches(current)) out.push(kid);
    }
    return out;
  };
  return walk(node && node.props ? node.props.children : []);
}

function hasGroup(node, group) { return partitionByGroup(node, group).length > 0; }

// Sub-pages put the long hint behind ⓘ (Alan: "long explanations should be in tool tips"),
// so the page is a list of settings and their values, not a wall of prose. The same
// SettingRow renders its hint inline everywhere else, unchanged. HintTip itself moved to
// Common.jsx (LB-265) when Settings needed the same affordance — one implementation, reused.
const HintsAsTooltips = React.createContext(false);

function SettingRow({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-2 border-t border-slate-200 py-2">
      <HintsAsTooltips.Consumer>
        {(asTooltip) => (
          <div className="min-w-0">
            {/* The label gets its own <span>. With the ⓘ as a sibling of a bare text node in
                the same element, the row's text reads "Words per testi" — the same defect CI
                report 52 caught on the Settings cards, in a row that renders for every
                Manual sub-page setting. checkHintTips (scripts/harness/hint-tips.mjs) is
                what now holds this everywhere rather than card by card. */}
            <div className="text-xs text-slate-600 font-medium"><span>{label}</span>{hint && asTooltip ? <HintTip text={hint} /> : null}</div>
            {hint && !asTooltip && <div className="text-xs text-slate-400">{hint}</div>}
          </div>
        )}
      </HintsAsTooltips.Consumer>
      {children}
    </div>
  );
}

// `lead` (Alan, 30 Aug 2026) is the one-line instruction that turns a group heading into
// something a parent can act on — "Accept our recommendation, or change the level" above a
// level picker, "Select the tables you want them to practise" above the tables grid. Passed
// only in guided (wizard) mode: on the Parent Mode dashboard a parent already knows why
// they came, and the line would be noise on every visit.
function GroupLabel({ children, lead }) {
  return (
    <div className="mt-3 mb-1.5 first:mt-0">
      <div className="text-xs font-medium text-slate-400">{children}</div>
      {lead && <div className="text-xs text-slate-500 leading-snug mt-0.5">{lead}</div>}
    </div>
  );
}

// Module-level (NOT defined inside LearningAdminTab): components declared inside a
// component get a new identity every render, so React would remount their whole subtree on
// each autosave commit — resetting Stepper drafts and dropping input focus mid-typing.
function TabBar({ tabs, active, onSelect }) {
  return (
    <div className="flex bg-slate-100 rounded-lg p-1 gap-1 mb-3">
      {tabs.map((t) => (
        <button key={t.id} data-testid={`lea-tab-${t.id}`} onClick={() => onSelect(t.id)}
          className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-bold ${active === t.id ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// One subject accordion. `open`/`onToggle` come from the parent so a single openSection
// state keeps exactly one section open at a time.
function Section({ id, title, summary, violet, open, onToggle, guided, plain, children }) {
  // LB-19 Zip C: on a re-cut sub-page the accordion chrome is wrong — the page IS the
  // section, so it renders its children bare.
  if (plain) return <div data-testid={`lea-section-${id}`}>{children}</div>;
  // Guided (wizard) mode: no header button, no summary, no chevron. There is exactly one
  // section on screen and nothing to collapse it into, so an accordion header would be a
  // control that does nothing — and the guided heading above already names the step. The
  // testid is deliberately NOT emitted here: `lea-section-*` means "the tappable accordion
  // header" to eight specs, and a non-tappable div answering to the same name would make
  // those pass against a surface that can't do what they're asserting.
  if (guided) {
    return (
      <div className={`bg-white rounded-2xl shadow-sm border mb-3 ${violet ? "border-violet-100" : "border-slate-100"}`} data-testid={`lea-guided-${id}`}>
        <div className="px-4 py-3">{children}</div>
      </div>
    );
  }
  return (
    <div className={`bg-white rounded-2xl shadow-sm border mb-3 ${violet ? "border-violet-100" : "border-slate-100"}`}>
      <button data-testid={`lea-section-${id}`} aria-expanded={open} onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <span className={`text-sm font-bold ${violet ? "text-violet-800" : "text-slate-700"}`}>{title}</span>
        <span className="flex-1 text-right text-xs font-medium text-slate-400 truncate">{summary}</span>
        <span aria-hidden="true" className="text-slate-400 text-xs flex-shrink-0"><Icon id={open ? "ic-chevron-down" : "ic-forward"} size={14} className="inline-block" style={{ color: "currentColor" }} /></span>
      </button>
      {open && <div className="border-t border-slate-100 px-4 py-3">{children}</div>}
    </div>
  );
}

// A game's show/hide row — the first thing in every game pane, same dynamic aria labels as
// the old reward-card rows.
function GameToggleRow({ label, childName, enabled, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-1">
      <div>
        <div className="text-sm font-bold text-slate-700">{label}</div>
        <div className="text-xs text-slate-400 font-medium">{enabled ? `${childName} can play this` : `Hidden from ${childName}`}</div>
      </div>
      <ToggleSwitch on={enabled} label={enabled ? `Hide ${label} from ${childName}` : `Show ${label} to ${childName}`} onClick={onToggle} />
    </div>
  );
}

export function LearningAdminTab({ state, update, activeChild, showToast, defaultOpen = null, guideStep = null, guideHeader = true, embedded = false, onDetailOpen = null, funOnly = false, hideFun = false, onOpenTest = null, onSeeAllTests = null }) {
  // Which subject accordion is open (one at a time; null = all collapsed) and which tab is
  // active within each — remembered per subject so flicking between sections doesn't lose
  // your place inside them.
  const [openSection, setOpenSection] = useState(defaultOpen);
  const [engTab, setEngTab] = useState("spelling");
  const [mathsTab, setMathsTab] = useState("ttstreaks");
  const [funTab, setFunTab] = useState("arcade");

  // Guided (wizard) mode — see LE_GUIDE_STEPS above. The caller owns the cursor, so which
  // pane is on screen is derived, not state: there is nothing here for a parent to open or
  // close, and keeping the local state untouched means the same component still behaves
  // exactly as before the moment guideStep goes away. An unrecognised id falls back to
  // ordinary dashboard mode rather than rendering nothing.
  const guide = guideStep ? LE_GUIDE_STEPS.find((g) => g.id === guideStep) || null : null;
  const guideIndex = guide ? LE_GUIDE_STEPS.indexOf(guide) : -1;
  const shownSection = guide ? guide.section : openSection;
  // LB-19 Zip C: which subject sub-page is open on the Manual page, if any. Guided (wizard)
  // mode is untouched — it walks its six parts exactly as before, and this whole re-cut is
  // skipped for it. The parent settings page is what Alan asked to re-shape.
  const [manualDetail, setManualDetail] = useState(null); // { subject, group } | null
  const recut = !guide && !funOnly;
  // 13 Sep (Alan): "there should be one Back CTA which simply takes you back to where you
  // came from." Embedded, the host draws the back at the top level and this component draws
  // it inside a subject — so the host needs to know which of the two is live.
  React.useEffect(() => { if (onDetailOpen) onDetailOpen(!!manualDetail); }, [manualDetail, onDetailOpen]);

  const shownEngTab = guide && guide.section === "english" ? guide.tab : engTab;
  const shownMathsTab = guide && guide.section === "maths" ? guide.tab : mathsTab;

  // The one place that decides where a subject's body renders. Before the re-cut this was
  // simply `{shownEngTab === "spelling" && (<div>…)}`; that condition is now passed in, so
  // guided mode keeps byte-identical behaviour.
  // A row only appears if that subject actually has controls in that group — spelling, for
  // instance, has no separate Test group (its test settings live with the words), so its
  // card shows two rows, not three with an empty one.
  function manualGroupExists(subject, group) {
    // Custom words earn their own row (Alan's screen 3) but only when the subject is
    // actually on a custom list — otherwise the row would open an empty page.
    if (group === "custom") {
      if (subject === "spelling") return spellingIsCustom;
      // Vocabulary's word manager is browse-and-edit for WHATEVER list is chosen, not a
      // custom-only panel, so it has no separate custom page — picking "Custom (your own
      // words)" in its List page is the path there, unchanged.
      return false;
    }
    return (MANUAL_GROUP_MAP[subject] || []).includes(group);
  }

  // Summaries are shown only where the page already knows the answer — no invented text.
  function manualSummary(subject, group) {
    if (group === "list") {
      if (subject === "spelling") return spellingIsCustom ? ct("learnearn.manual.own_words", "{name}'s own words", { name: activeChild.name }) : effectiveSpellingLists()[spellingListKey].label;
      if (subject === "vocab") return resolvedVocabCategory === "custom" ? ct("learnearn.manual.own_words", "{name}'s own words", { name: activeChild.name }) : effectiveVocabCategories()[resolvedVocabCategory].label;
      if (subject === "mathsworkout") return effectiveMathsBands()[resolveMathsBand(activeChild, content.mathsBand)].label;
      if (subject === "ttstreaks") {
        const tabs = content.ttTables && content.ttTables.length > 0 ? content.ttTables : [2, 3, 4, 5, 10];
        return ct("learnearn.manual.tables_summary", "{n} tables · {list}", { n: tabs.length, list: [...tabs].sort((a, b) => a - b).join(", ") });
      }
    }
    return null;
  }

  function subjectSlot(subject, legacyCondition, node) {
    if (!recut) return legacyCondition ? node : null;
    if (!manualDetail) return null;
    // "What English pays" / "What Maths pays" summarise across the two subjects in their
    // area, so they belong on those subjects' Reward pages rather than nowhere.
    const isPayoutSummary = subject === "english" || subject === "maths";
    const belongs = isPayoutSummary
      ? manualDetail.group === "reward" && (subject === "english" ? ENGLISH_SUBJECTS : MATHS_SUBJECTS).includes(manualDetail.subject)
      : manualDetail.subject === subject;
    if (!belongs) return null;
    return <HintsAsTooltips.Provider value={true}>{partitionByGroup(node, manualDetail.group)}</HintsAsTooltips.Provider>;
  }

  const content = state.learningContent[activeChild.id] || { spellingWords: [], vocabBank: [] };

  // --- Spelling management (custom word list) ---
  const [word, setWord] = useState("");
  const [bulkWords, setBulkWords] = useState("");
  const [showBulkWords, setShowBulkWords] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false); // LB-363: photograph a list into the bulk box
  const [photoFlags, setPhotoFlags] = useState([]);
  // Alan, 27 Sep: "Photograph a list" opens the phone's picker straight away (the tap itself
  // clicks the input; iOS ignores a later click), and the chosen photo goes to PhotoCapture.
  const [photoFile, setPhotoFile] = useState(null);
  const photoInputRef = useRef(null);
  const [spellSelectMode, setSpellSelectMode] = useState(false);
  const [selectedWords, setSelectedWords] = useState([]);

  // --- Vocabulary management (word bank) ---
  const [vWord, setVWord] = useState("");
  const [vDef, setVDef] = useState("");
  const [vD1, setVD1] = useState("");
  const [vD2, setVD2] = useState("");
  const [vD3, setVD3] = useState("");
  const [bulkVocabText, setBulkVocabText] = useState("");
  const [showBulkVocab, setShowBulkVocab] = useState(false);
  // Word-bank finding aids: free-text search (wins while non-empty) and the A–Z letter
  // filter. Search was added in the 2026-08 redesign — finding one word in a 1,380-word
  // bank previously required knowing its first letter.
  const [vocabSearch, setVocabSearch] = useState("");
  const [vocabLetterFilter, setVocabLetterFilter] = useState(null);
  // Destructive selection lives behind an explicit Select mode now, so the default list
  // view is calm — no always-on checkbox column. Custom words only, as ever.
  const [vocabSelectMode, setVocabSelectMode] = useState(false);
  const [selectedVocab, setSelectedVocab] = useState([]);

  useEffect(() => {
    setSelectedWords([]); setSpellSelectMode(false);
    setSelectedVocab([]); setVocabSelectMode(false);
    setVocabSearch(""); setVocabLetterFilter(null);
    // Sections and tabs deliberately stay put on child switch — a parent working through
    // "spelling for each kid" keeps their place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChild.id]);

  // Autosave writer for flat learningContent fields — same ensure-exists defensiveness as
  // setArcade below (preview-mode and pre-migration profiles may have no entry at all).
  // Adaptive pin (§5): freezes the working band; unpinning lets it move again.
  function setAdaptivePin(game, pinned, byAge) {
    update((s) => {
      const lc = s.learningContent[activeChild.id]; if (!lc) return null;
      lc.adaptive = lc.adaptive || {};
      const st = adaptiveGameState(lc.adaptive, game, byAge);
      st.pinned = pinned; lc.adaptive[game] = st;
      // LB-343: the sixth argument used to be activeChild.name, which logSetting has never had a
      // parameter for — it was silently dropped on every call. It is the acting ADULT now,
      // which is what the sixth parameter means and what G2 draws.
      logSetting(s, getDeviceId(), `adaptive.${game}.pinned`, pinned ? "no" : "yes", pinned ? "yes" : "no", actor(s));
      return s;
    });
    showToast(pinned ? ct("adaptive.toast.pinned", "Pinned. The level stays until you let it move.") : ct("adaptive.toast.unpinned", "Learning Coach can move the level again."), "good");
  }
  function setLC(patch) {
    update((s) => {
      if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] };
      Object.assign(s.learningContent[activeChild.id], patch);
      return s;
    });
  }

  function gameEnabled(id) { return content[`${id}Enabled`] !== false; }
  function toggleGameEnabled(id) {
    update((s) => { const lc = s.learningContent[activeChild.id]; lc[`${id}Enabled`] = !(lc[`${id}Enabled`] !== false); return s; });
  }

  // R5 — Piggington's Arcade settings live under learningContent[childId].arcade (master
  // visibility toggle + warm-up mode). Write-time initialiser mirrors ensureCosmetics's
  // "fields may be absent" defensiveness: preview-mode and pre-migration profiles have no
  // arcade object at all, and absence must mean the defaults (visible, Warm-Up required).
  function setArcade(patch) {
    update((s) => {
      if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] };
      const lc = s.learningContent[activeChild.id];
      if (!lc.arcade || typeof lc.arcade !== "object") lc.arcade = { snackCatchEnabled: true, warmupMode: "warmup" };
      Object.assign(lc.arcade, patch);
      return s;
    });
  }

  // Cross-field advisory (was a hard Save block): with independent autosaving steppers a
  // parent must be able to move one value through a temporarily inverted state, so we warn
  // instead of refusing.
  function advisePair(passVal, fullVal) {
    if (fullVal < passVal) showToast(ct("toast.reward_below_pass", "The 100% reward is now below the pass reward"), "bad");
  }

  function toggleTtTable(n) {
    const current = content.ttTables && content.ttTables.length > 0 ? content.ttTables : [2, 3, 4, 5, 10];
    if (current.includes(n) && current.length === 1) { showToast(ct("toast.tt_need_one_table", "Switch on at least one times table"), "bad"); return; }
    const next = current.includes(n) ? current.filter((x) => x !== n) : [...current, n].sort((a, b) => a - b);
    update((s) => { s.learningContent[activeChild.id].ttTables = next; return s; });
  }

  // Ladder preset chips (TT_YEAR_PRESETS): one tap replaces the set wholesale — no
  // per-table confirmation, same directness as toggleTtTable, and never empty so the
  // at-least-one-table guard can't be tripped from here.
  function applyTtPreset(tables) {
    update((s) => { s.learningContent[activeChild.id].ttTables = [...tables]; return s; });
  }

  // TT session-size commit re-derives the streak target to 80% (rounded) and clamps it into
  // range, exactly as the old form did live — the two numbers are meant to move together
  // unless a parent deliberately breaks the relationship by editing the target afterward.
  function commitTtSessionSize(n) {
    const target = Math.max(1, Math.min(n, Math.round(n * 0.8)));
    setLC({ ttSessionSize: n, ttStreakPassCount: target });
  }

  // --- Custom spelling list actions ---
  // Queues text for the shared word bank (TTS_SCOPE.md §5.3), inside the SAME update()
  // call as the state change it accompanies — one household-state write, not a second
  // action. App.jsx's flush effect sends these a few seconds later, and the server voices
  // what passes the rude-word filter straight away; this function only touches local state.
  function queueWordbank(s, texts) {
    s.pendingWordbankSubmissions = [...(s.pendingWordbankSubmissions || []), ...texts];
  }

  function addWord() {
    if (!word.trim()) return;
    if (content.spellingWords.length >= SPELLING_CAP) { showToast(ct("toast.spelling_cap", "Spelling list capped at {cap} words", { cap: SPELLING_CAP }), "bad"); return; }
    const w = word.trim();
    update((s) => { s.learningContent[activeChild.id].spellingWords.push({ id: uid(), word: w, testInclude: true }); queueWordbank(s, [w]); return s; });
    setWord("");
  }
  function toggleWordTestInclude(id) {
    update((s) => {
      const w = s.learningContent[activeChild.id].spellingWords.find((x) => x.id === id);
      if (w) w.testInclude = w.testInclude === false ? true : false;
      return s;
    });
  }
  function setAllWordsTestInclude(val) {
    update((s) => { s.learningContent[activeChild.id].spellingWords.forEach((w) => { w.testInclude = val; }); return s; });
  }
  function toggleWordSelected(id) { setSelectedWords((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  function deleteSelectedWords() {
    const n = selectedWords.length;
    update((s) => { s.learningContent[activeChild.id].spellingWords = s.learningContent[activeChild.id].spellingWords.filter((w) => !selectedWords.includes(w.id)); return s; });
    setSelectedWords([]); setSpellSelectMode(false);
    showToast(ct("toast.words_deleted", "Deleted {n} word{plural}", { n, plural: n === 1 ? "" : "s" }), "bad");
  }
  function bulkAddWords() {
    const existing = new Set(content.spellingWords.map((w) => w.word.toLowerCase()));
    const raw = bulkWords.split(/[\n,]/).map((w) => w.trim()).filter(Boolean);
    // LB-377 follow-up (Alan, 27 Sep): the other door into a word list. A word bank feeds the
    // child's games and a list built from it can be shared, so nothing rude is added at all —
    // the whole paste is refused and named, rather than one word being dropped quietly.
    const rude = raw.filter(containsRudeWord);
    if (rude.length) { showToast(ct("admin.words.bulk_rude", "{word} cannot go in a word list. Take it out and try again.", { word: rude[0] }), "info"); return; }
    const toAdd = [];
    let dupes = 0;
    for (const w of raw) {
      const key = w.toLowerCase();
      if (existing.has(key)) { dupes++; continue; }
      existing.add(key); toAdd.push(w);
    }
    const room = SPELLING_CAP - content.spellingWords.length;
    const applied = toAdd.slice(0, Math.max(0, room));
    const skippedCap = toAdd.length - applied.length;
    if (applied.length > 0) update((s) => { s.learningContent[activeChild.id].spellingWords.push(...applied.map((w) => ({ id: uid(), word: w, testInclude: true }))); queueWordbank(s, applied); return s; });
    let msg = `Added ${applied.length} word${applied.length === 1 ? "" : "s"}`;
    if (dupes) msg += `, ${dupes} duplicate${dupes === 1 ? "" : "s"} skipped`;
    if (skippedCap) msg += `, ${skippedCap} skipped (cap reached)`;
    showToast(msg, applied.length > 0 ? "good" : "bad");
    setBulkWords(""); setShowBulkWords(false); setPhotoFlags([]);
  }

  // R3a — weekly auto-serve config. Stored as lc.spellingAuto = { enabled, list, batchSize };
  // every field is read with a default (on / "auto" / SPELLING_AUTO_DEFAULT_BATCH), so
  // households that have never touched this card need no migration and get auto-serve on.
  function setSpellingAuto(patch) {
    update((s) => {
      const lc = s.learningContent[activeChild.id];
      lc.spellingAuto = { ...(lc.spellingAuto || {}), ...patch };
      return s;
    });
  }

  // R3a + staging feedback (2026-08): the word-source picker is the single control — no
  // separate on/off switch. Selecting "Custom list" is the same thing as selecting no
  // curriculum list; selecting a real list also unticks any lingering custom words, so the
  // batch actually takes effect right away rather than leaving a hidden override active
  // now that the custom-words section collapses out of view for that choice.
  // Choosing a concrete list/band by hand is an override: Adaptive stops adjusting that game
  // and does NOT come back on its own. The hub's Change CTA says so in a sheet; this path
  // said nothing at all until the 12 Sep staging pass, so it says it here.
  function warnManualOverride(game, newVal) {
    if (newVal === "auto" || !coachEnabled(state.settings)) return;
    showToast(ct("learnearn.manual.override_warning", "{game} is now manual. Learning Coach stays off for it until you set it back.", { game }));
  }

  function onSpellingListChange(newVal) {
    warnManualOverride(ct("learnearn.manual.game_spelling", "Spelling"), newVal);
    if (newVal === "off") { setSpellingAuto({ list: newVal }); return; }
    update((s) => {
      const lc = s.learningContent[activeChild.id];
      lc.spellingAuto = { ...(lc.spellingAuto || {}), list: newVal };
      lc.spellingWords.forEach((w) => { w.testInclude = false; });
      return s;
    });
  }

  // --- Vocabulary bank actions ---
  // Every vocab word carries `source` ("custom" for a parent's own entries, or a
  // VOCAB_CATEGORY_ORDER key for one added from a starter list), `testInclude` (the
  // include/skip-from-rotation toggle), and `correctCount` (how many times it's been
  // answered correctly — see App.jsx's onVocabWordResult; reaching 2 retires it from the
  // eligible pool VocabTest draws from until a parent resets it below).
  function addVocab() {
    if (!vWord.trim() || !vDef.trim() || !vD1.trim() || !vD2.trim() || !vD3.trim()) return;
    const w = vWord.trim();
    update((s) => { s.learningContent[activeChild.id].vocabBank.push({ id: uid(), word: w, definition: vDef.trim(), options: [vDef.trim(), vD1.trim(), vD2.trim(), vD3.trim()], source: "custom", testInclude: true, correctCount: 0 }); queueWordbank(s, [w]); return s; });
    setVWord(""); setVDef(""); setVD1(""); setVD2(""); setVD3("");
  }
  function toggleVocabTestInclude(id) {
    update((s) => {
      const v = s.learningContent[activeChild.id].vocabBank.find((x) => x.id === id);
      if (v) v.testInclude = v.testInclude === false ? true : false;
      return s;
    });
  }
  // Lets a parent bring a mastered word (2 correct answers) back into rotation without
  // waiting for it to somehow "un-master" itself — there's no other way back in otherwise.
  function resetVocabMastery(id) {
    update((s) => { const v = s.learningContent[activeChild.id].vocabBank.find((x) => x.id === id); if (v) v.correctCount = 0; return s; });
  }
  function toggleVocabSelected(id) { setSelectedVocab((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); }
  // Custom words only — starter-list rows never get a selection checkbox in the first
  // place (see renderVocabRow), and the filter here is belt-and-braces on top of that.
  function deleteSelectedVocab() {
    const custom = new Set(content.vocabBank.filter((v) => !v.source || v.source === "custom").map((v) => v.id));
    const doomed = selectedVocab.filter((id) => custom.has(id));
    const n = doomed.length;
    if (n === 0) { setVocabSelectMode(false); setSelectedVocab([]); return; }
    update((s) => { s.learningContent[activeChild.id].vocabBank = s.learningContent[activeChild.id].vocabBank.filter((v) => !doomed.includes(v.id)); return s; });
    setSelectedVocab([]); setVocabSelectMode(false);
    showToast(ct("toast.words_deleted", "Deleted {n} word{plural}", { n, plural: n === 1 ? "" : "s" }), "bad");
  }
  function bulkAddVocab() {
    const lines = bulkVocabText.split("\n").map((l) => l.trim()).filter(Boolean);
    const rudeLine = lines.find(containsRudeWord);
    if (rudeLine) { showToast(ct("admin.words.bulk_rude_line", "One of those lines has a word that cannot go in a word list. Take it out and try again."), "info"); return; }
    const toAdd = [];
    let invalid = 0;
    for (const line of lines) {
      const parts = line.split("|").map((p) => p.trim());
      if (parts.length !== 5 || parts.some((p) => !p)) { invalid++; continue; }
      const [w, def, d1, d2, d3] = parts;
      toAdd.push({ id: uid(), word: w, definition: def, options: [def, d1, d2, d3], source: "custom", testInclude: true, correctCount: 0 });
    }
    if (toAdd.length > 0) update((s) => { s.learningContent[activeChild.id].vocabBank.push(...toAdd); queueWordbank(s, toAdd.map((w) => w.word)); return s; });
    let msg = `Added ${toAdd.length} vocab word${toAdd.length === 1 ? "" : "s"}`;
    if (invalid) msg += `, ${invalid} line${invalid === 1 ? "" : "s"} skipped (check format)`;
    showToast(msg, toAdd.length > 0 ? "good" : "bad");
    setBulkVocabText(""); setShowBulkVocab(false);
  }

  // Activating a source list: `source` on top of `testInclude`. Every word in the newly
  // active list defaults back to "in test" — including mastered ones, whose exclusion is
  // handled by the eligibility filter so R3b refreshers can serve (see the inline note
  // below) — and a parent can still skip individual words afterward from the list below.
  // Custom words are never touched by this beyond being switched out of rotation while a
  // starter list is active — see Alan's 2026-08-15 scoping note ("don't auto-delete custom
  // words").
  function activateVocabSource(rawValue) {
    const resolved = resolveVocabCategory(activeChild, rawValue);
    update((s) => {
      const lc = s.learningContent[activeChild.id];
      lc.vocabCategory = rawValue;
      if (!lc.vocabBank) lc.vocabBank = [];
      // First time this starter category becomes active, load its words in (custom never
      // auto-populates — a parent types or pastes those themselves).
      //
      // BUG FIX (2026-08-16): "already in the bank" used to be checked against the WHOLE
      // bank (every category ever loaded, plus custom), not just this category's own words.
      // The starter lists deliberately reuse a lot of the same headwords across categories
      // (same core vocabulary, harder definitions/distractors at higher levels), so once one
      // category had loaded, switching to a different one saw almost every word as "already
      // present" and skipped adding it — e.g. Developing Readers loaded only the ~9 words it
      // doesn't share with Early Readers instead of its full 325. Scoping the check to only
      // this category's own existing entries (source === resolved) fixes it, and still keeps
      // re-selecting the same category from adding duplicate rows.
      if (resolved !== "custom") {
        const list = effectiveStarterVocabList(resolved);
        const existing = new Set(lc.vocabBank.filter((v) => v.source === resolved).map((v) => v.word.toLowerCase()));
        // Same speakAs-dropping bug as effectiveStarterVocabList itself had (see its own
        // fix comment) — this constructs its own object naming only the fields it already
        // knew about, so fixing the source function alone wasn't enough; every hop that
        // reshapes a word into a new object needs the same fix independently.
        const toAdd = list.filter((w) => !existing.has(w.word.toLowerCase()))
          .map((w) => {
            const item = { id: uid(), word: w.word, definition: w.definition, options: [w.definition, ...(w.distractors || [])], source: resolved, testInclude: true, correctCount: 0 };
            if (w.speakAs) item.speakAs = w.speakAs;
            return item;
          });
        lc.vocabBank.push(...toAdd);
      }
      lc.vocabBank.forEach((v) => {
        const inNewList = resolved === "custom" ? (!v.source || v.source === "custom") : v.source === resolved;
        // Every word in the newly active list goes back into rotation — including mastered
        // ones. Mastery exclusion is handled by the eligibility filter in ChildView's
        // LearnEarnTab (correctCount < 2 || refresher due), and R3b's spaced-repetition
        // refreshers can only serve if a mastered word's testInclude stays true. (This used
        // to set mastered words to false, which would have silently blocked refreshers for
        // any re-activated list.) A parent can still skip individual words below.
        v.testInclude = inNewList;
      });
      return s;
    });
    setVocabLetterFilter(null);
    setVocabSearch("");
  }
  // Applies to the currently active list only (mirrors setAllWordsTestInclude for spelling,
  // scoped down since vocab only ever shows one list at a time).
  function setAllVocabTestInclude(val) {
    update((s) => {
      const lc = s.learningContent[activeChild.id];
      lc.vocabBank.forEach((v) => {
        const inList = resolvedVocabCategory === "custom" ? (!v.source || v.source === "custom") : v.source === resolvedVocabCategory;
        if (inList) v.testInclude = val;
      });
      return s;
    });
  }

  // 2026-08 redesign: word rows say their status in words instead of three unlabelled
  // controls — "In tests · learning (n of 2)" / "In tests · mastered ✓✓ (tappable reset)" /
  // "Skipped — not in tests" — with one Include/Skip action. In Select mode, custom words
  // get a checkbox for bulk delete; starter words get a spacer (they can be skipped but
  // never deleted, as ever).
  function renderVocabRow(v) {
    const mastered = (v.correctCount || 0) >= 2;
    const isCustom = !v.source || v.source === "custom";
    const included = v.testInclude !== false;
    return (
      <div key={v.id} className="flex items-center gap-2 py-2 border-b border-slate-50">
        {vocabSelectMode && (isCustom
          ? <input type="checkbox" className="flex-shrink-0" checked={selectedVocab.includes(v.id)} onChange={() => toggleVocabSelected(v.id)} aria-label={`Select ${v.word} for delete`} />
          : <span className="w-[13px] flex-shrink-0" />)}
        <div className="flex-1 min-w-0">
          <div className={`text-sm truncate ${included ? "text-slate-700" : "text-slate-300"}`}>{v.word}</div>
          <div className={`text-xs font-medium ${included ? "text-slate-400" : "text-slate-300"}`}>
            {included ? (
              mastered
                ? <>In tests · <button className="text-emerald-600 bg-emerald-50 rounded-lg px-1.5 font-bold" title="Answered correctly twice — tap to put back in the draw" onClick={() => resetVocabMastery(v.id)}><span className="inline-flex items-center gap-0.5">mastered<Icon id="ic-check" size={12} style={{ color: "currentColor" }} /><Icon id="ic-check" size={12} className="-ml-1.5" style={{ color: "currentColor" }} /></span></button></>
                : `In tests · learning (${Math.min(v.correctCount || 0, 2)} of 2)`
            ) : "Skipped — not in tests"}
          </div>
        </div>
        <button className={`text-xs font-bold flex-shrink-0 ${included ? "text-slate-500" : "text-teal-600"}`} onClick={() => toggleVocabTestInclude(v.id)}>{included ? "Skip" : "Include"}</button>
      </div>
    );
  }

  // Suggested/selected category for this child (parent override always wins — see
  // activateVocabSource) and the words that make up the one currently active list — either
  // one starter category or "custom". Nothing outside this list is shown or eligible; see
  // ChildView.jsx's LearnEarnTab for the matching eligibility filter.
  const resolvedVocabCategory = resolveVocabCategory(activeChild, content.vocabCategory);
  const activeVocabWords = useMemo(() => content.vocabBank.filter((v) =>
    resolvedVocabCategory === "custom" ? (!v.source || v.source === "custom") : v.source === resolvedVocabCategory
  ), [content.vocabBank, resolvedVocabCategory]);
  const vocabAvailableLetters = useMemo(() => new Set(activeVocabWords.map((v) => (v.word[0] || "").toUpperCase())), [activeVocabWords]);
  const vocabVisibleWords = useMemo(() => {
    const q = vocabSearch.trim().toLowerCase();
    let list = activeVocabWords;
    if (q) list = list.filter((v) => v.word.toLowerCase().includes(q));
    else if (vocabLetterFilter) list = list.filter((v) => v.word[0]?.toUpperCase() === vocabLetterFilter);
    return [...list].sort((a, b) => a.word.localeCompare(b.word));
  }, [activeVocabWords, vocabLetterFilter, vocabSearch]);

  // BUG FIX (2026-08-16): this used to only fire when the WHOLE bank was empty
  // (content.vocabBank.length === 0), meant to cover a brand new child's first-ever visit.
  // But once any category (or custom words) had loaded for a child, that guard permanently
  // blocked ever auto-loading a DIFFERENT category — e.g. age-driven "auto" correctly
  // resolved to Developing Readers, but since the bank already had Early Readers' words in
  // it from an earlier visit, this effect never ran, activeVocabWords stayed empty, and the
  // "coming soon" message below showed even though the starter list has 325 words. Checking
  // the ACTIVE list's own emptiness (against the starter list's real content, so it doesn't
  // fire pointlessly for genuinely empty categories) fixes both the original first-visit
  // case and this one. Post-redesign, "the vocab pane is on screen" means the English
  // section is open on the Vocabulary tab — the same gate the old selected-game state
  // provided, so starter words still only load once a parent actually looks at vocab.
  useEffect(() => {
    if (!(shownSection === "english" && shownEngTab === "vocab") || resolvedVocabCategory === "custom") return;
    const hasWordsForActive = content.vocabBank.some((v) => v.source === resolvedVocabCategory);
    const starterHasWords = effectiveStarterVocabList(resolvedVocabCategory).length > 0;
    if (!hasWordsForActive && starterHasWords) activateVocabSource(content.vocabCategory ?? "auto");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownSection, shownEngTab, activeChild.id, resolvedVocabCategory]);

  // Collapsed-header status summaries — what makes the all-collapsed page read as a
  // dashboard rather than three shut doors.
  function sectionSummary(id) {
    if (id === "english") {
      const on = ENGLISH_GAMES.filter((g) => gameEnabled(g.id)).length;
      return `${on} of 2 games on · max ${content.learnEarnPayoutsPerWeek ?? 1}×/wk`;
    }
    if (id === "maths") {
      const on = MATHS_GAMES.filter((g) => gameEnabled(g.id)).length;
      return `${on} of 2 games on · max ${content.mathsPayoutsPerWeek ?? 1}×/wk`;
    }
    return `Arcade ${arcadeEnabledFor(content) ? "on" : "off"} · Oinks ${content.oinksEnabled !== false ? "on" : "off"}`;
  }

  // --- Values (stored ?? default), read once per render for the steppers ---
  const spellingRewardVal = content.spellingReward ?? 5;
  const vocabTestSizeVal = content.vocabTestSize ?? VOCAB_TEST_SIZE;
  const vocabPassRewardVal = content.vocabPassReward ?? 3;
  const vocabFullRewardVal = content.vocabFullReward ?? 5;
  const vocabPassThresholdVal = content.vocabPassThreshold ?? DEFAULT_PASS_THRESHOLD;
  const englishPayoutsVal = content.learnEarnPayoutsPerWeek ?? 1;
  const ttSessionSizeVal = content.ttSessionSize ?? TT_SESSION_SIZE;
  const ttStreakPassCountVal = content.ttStreakPassCount ?? Math.round(ttSessionSizeVal * 0.8);
  const ttStreakPassRewardVal = content.ttStreakPassReward ?? 3;
  const ttStreakFullRewardVal = content.ttStreakFullReward ?? 5;
  const mathsTestSizeVal = content.mathsTestSize ?? MATHS_WORKOUT_SESSION_SIZE;
  const mathsPassThresholdVal = content.mathsPassThreshold ?? DEFAULT_PASS_THRESHOLD;
  const mathsPassRewardVal = content.mathsPassReward ?? 3;
  const mathsFullRewardVal = content.mathsFullReward ?? 5;
  const mathsPayoutsVal = content.mathsPayoutsPerWeek ?? 1;
  const mathsTimerVal = content.mathsTimerSeconds ?? 0;

  const spellingAuto = content.spellingAuto || {};
  const spellingListKey = resolveSpellingList(activeChild, spellingAuto.list);
  // Which games Learning Coach is actually running for this child — the tests draw
  // coachTestSize words then, and the manual length controls (spelling's weekly batch,
  // vocab's question count) do not apply, so they are replaced by a line that names the
  // number that does (Alan, 22 Sep: "I was confused by the test size, too").
  const coachSizeVal = content.coachTestSize || DEFAULT_COACH_TEST_SIZE;
  const coachRunsSpelling = coachEnabled(state.settings) && (!spellingAuto.list || spellingAuto.list === "auto");
  const coachRunsVocab = coachEnabled(state.settings) && (!content.vocabCategory || content.vocabCategory === "auto");
  const spellingPassThresholdVal = content.spellingPassThreshold ?? DEFAULT_PASS_THRESHOLD;
  const spellingIsCustom = spellingListKey === "off";
  const spellingBatchVal = spellingAuto.batchSize || SPELLING_AUTO_DEFAULT_BATCH;
  const customTickedCount = content.spellingWords.filter((w) => w.testInclude !== false).length;
  const spellingWeekBatch = !spellingIsCustom ? autoSpellingBatch(spellingListKey, weekStartOf(todayStr()), spellingAuto.batchSize) : [];


  // LB-19 Zip B (screen 4, signed off): Just for fun leaves Learn & Earn — KidDetail mounts
  // this component with funOnly for its own "Just for fun" row, and the Learn & Earn mounts
  // pass hideFun so Arcade and Oinks no longer sit among the paying games. The body below is
  // the exact block that lived inside the fun <Section>, moved verbatim.
  const funBody = (
    <div data-testid="fun-body">
        <TabBar tabs={[{ id: "arcade", label: "Arcade" }, { id: "oinks", label: "Oinks" }]} active={funTab} onSelect={setFunTab} />

        {funTab === "arcade" && (
          <div data-testid="arcade-admin-card">
            {/* R5 — Piggington's Arcade (Snack Attack, and Pig Mountain since LB-256: this one
                switch and this one warm-up choice cover both). The entire parent surface is this
                pane: a master visibility toggle (hide-without-losing-settings, default ON —
                a zero-setup feature whose ABSENCE is what a parent would notice) and the
                warm-up mode, Brain Warm-Up pre-selected. It pays nothing by design (Law 1 —
                see PIGGINGTONS_ARCADE_SCOPE.md). */}
            <div className="flex items-center gap-2.5 mb-2">
              <span className="flex-shrink-0" aria-hidden="true"><Icon id="ic-arcade" size={22} hue="indigo" /></span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-700">Piggington's Arcade</div>
                <div className="text-xs text-slate-400 font-medium">Fun only — no tokens or Oinks</div>
              </div>
              <ToggleSwitch on={arcadeEnabledFor(content)} label="Arcade on or off" onClick={() => setArcade({ snackCatchEnabled: !arcadeEnabledFor(content) })} />
            </div>
            <p className="text-xs text-slate-500 leading-relaxed mb-2">{ct("learnearn.arcade_body", "Two mini-games, Snack Attack and Pig Mountain, playable even with no internet. They never pay tokens or Oinks — just a personal best to beat.")}</p>
            <div className="flex flex-col gap-1.5">
              <button
                data-testid="arcade-warmup-required"
                onClick={() => setArcade({ warmupMode: "warmup" })}
                className={`w-full text-left rounded-xl border px-3 py-2 ${arcadeWarmupModeFor(content) === "warmup" ? "border-pink-400 bg-pink-50" : "border-slate-200"}`}
              >
                <div className="text-xs font-bold text-slate-700"><Icon id="ic-brain" size={14} hue="indigo" className="inline-block align-[-2px] mr-1" />{ct("learnearn.warmup_title", "Brain Warm-Up")} <span className="text-pink-500">(recommended)</span></div>
                <div className="text-xs text-slate-500 font-medium">{ct("learnearn.warmup_sub", "3 quick maths questions each day before playing")}</div>
              </button>
              <button
                data-testid="arcade-warmup-free"
                onClick={() => setArcade({ warmupMode: "free" })}
                className={`w-full text-left rounded-xl border px-3 py-2 ${arcadeWarmupModeFor(content) === "free" ? "border-pink-400 bg-pink-50" : "border-slate-200"}`}
              >
                <div className="text-xs font-bold text-slate-700"><Icon id="ic-arcade" size={14} hue="indigo" className="inline-block align-[-2px] mr-1" />{ct("learnearn.freeplay_title", "Free play")}</div>
                <div className="text-xs text-slate-500 font-medium">{ct("learnearn.freeplay_sub", "Straight into the game, no questions")}</div>
              </button>
            </div>
          </div>
        )}

        {funTab === "oinks" && (
          <div data-testid="oinks-admin-card">
            {/* Oinks — the fun-only currency (see lib/oinks.js). Default opt-in —
                oinksEnabled defaults true whenever unset. The ENTIRE parent surface is this
                pane: an on/off toggle, a read-only balance, and the Law-1 explainer — zero
                curation burden by design. Off hides every kid-side Oink surface (Piggington
                card, shop, in-game rows) and pauses earning, but keeps the balance and owned
                items so switching back on later loses nothing. */}
            <div className="flex items-center gap-2.5 mb-2">
              <OinkIcon className="w-7 h-7 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-700">{ct("learnearn.oinks_title", "Oinks & the Oink Shop")}</div>
                <div className="text-xs text-slate-400 font-medium">{ct("learnearn.oinks_sub", "Fun rewards for practising")}</div>
              </div>
              <ToggleSwitch on={content.oinksEnabled !== false} label="Oinks on or off" onClick={() => update((s) => { if (!s.learningContent[activeChild.id]) s.learningContent[activeChild.id] = { spellingWords: [], vocabBank: [] }; const lc = s.learningContent[activeChild.id]; lc.oinksEnabled = !(lc.oinksEnabled !== false); return s; })} />
            </div>
            <p className="text-xs text-slate-500 leading-relaxed mb-2">{activeChild.name} earns <b>Oinks</b> for every Learn & Earn question answered correctly — even after hitting the weekly earning limit — and spends them on outfits for the pig and their avatar.</p>
            <p className="text-xs font-bold text-pink-600 bg-pink-50 rounded-lg px-2.5 py-2 mb-2">Oinks are just for fun. They're never worth money and can't be converted to tokens.</p>
            {/* LB-168: Give Oinks — the birthday lever and the way to close a gap of a few Oinks.
                1–50 per go, no weekly ceiling (Alan). Never money; logged on the child's ledger
                and as a transaction of kind "oink_gift" so it shows in Recent activity. */}
            <GiveOinks child={activeChild} update={update} showToast={showToast} />
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-2.5 py-2">
              <span className="text-xs font-medium text-slate-400">{activeChild.name}'s balance</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-pink-600"><OinkIcon className="w-4 h-4" /> {childOinks(activeChild)} Oinks</span>
            </div>
          </div>
        )}
    </div>
  );
  if (funOnly) return funBody;

  return (
    <div>
      {guide && guideHeader ? (
        /* Guided (wizard) header. Replaces the "Learn & Earn / Settings for {name}" title —
           the wizard's own intro card and ChildContextBar already say both of those things
           directly above, so repeating them here just pushed the actual controls further
           down the screen. What a parent gets instead is the one thing that was missing:
           where they are in the sequence, and what this screen is for. */
        <div className="mb-3" data-testid="lea-guide-header">
          <div className="text-xs font-semibold text-teal-600">
            {ct("learnearn.guide.position", "Part {n} of {total} · {label}", { n: guideIndex + 1, total: LE_GUIDE_STEPS.length, label: guide.label })}
          </div>
          <div className="text-lg font-extrabold text-slate-800 leading-tight">{ct(`learnearn.guide.heading_${guide.id}`, guide.heading)}</div>
          <p className="text-xs text-slate-500 leading-snug mt-1">{ct(`learnearn.guide.todo_${guide.id}`, guide.todo)}</p>
        </div>
      ) : (
        /* 2026-08 redesign: the big sticky gradient "Editing for {name}" banner is gone —
            reverted to a prominent plain text header (Alan: it took too much space; the sticky
            child-chip row above already answers "which child" mid-scroll). */
        embedded ? null : (
        <div className="mb-3">
          <div className="text-xl font-extrabold text-slate-800 leading-tight">Learn &amp; Earn</div>
          <div className="text-xs font-medium text-slate-400">Settings for {activeChild.name}</div>
        </div>
        )
      )}

      {/* ===================== THE RE-CUT MANUAL PAGE (LB-19 Zip C) ===================== */}
      {recut && !manualDetail && (
        <div className="flex flex-col gap-3" data-testid="manual-subjects">
          {coachEnabled(state.settings) && (
            <p className="text-xs text-slate-600 leading-relaxed bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2.5 m-0" data-testid="manual-coach-note">
              {ct("learnearn.manual.coach_note", "You don't need to set any of this: Learning Coach is handling it. Change what you like, and when you are done, hand that game back so it starts moving again.")}
            </p>
          )}
          {MANUAL_SUBJECTS.map((subj) => (
            <div key={subj.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`manual-card-${subj.id}`}>
              <div className="px-3 pt-2.5 pb-1 text-xs font-bold text-slate-800">{subj.label}</div>
              {MANUAL_GROUPS.filter((g) => manualGroupExists(subj.id, g.id)).map((g) => (
                <button key={g.id} type="button" onClick={() => setManualDetail({ subject: subj.id, group: g.id })}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-2 border-t border-slate-100"
                  data-testid={`manual-row-${subj.id}-${g.id}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-medium text-slate-600">{g.label}</span>
                    {manualSummary(subj.id, g.id) && <span className="block text-xs text-slate-400 mt-0.5">{manualSummary(subj.id, g.id)}</span>}
                  </span>
                  <Icon id="ic-forward" size={12} className="text-slate-300 flex-shrink-0" style={{ color: "currentColor" }} />
                </button>
              ))}
              {/* Households with Learning Coach OFF have no Coach's summary, so this is the
                  one place their tests can show (P4, signed off 22 Sep). Fed by the wrapper
                  from this release — nothing was recorded with Coach off before it, which is
                  why the card starts empty rather than backfilled. Coach-on households read
                  the same log in Coach's summary and skip this card. */}
              {/* LB-366: the same card Coach's game detail uses — history rows open the test,
                  and "See all" opens the history on this game (only where the hub supplied the
                  routes; the wizard and Kids-tab mounts show the rows without them). */}
              {!coachEnabled(state.settings) && (
                <RecentTestsCard variant="manual" learning={content} game={subj.id} today={todayStr()} onOpenTest={onOpenTest} onSeeAll={onSeeAllTests} />
              )}
            </div>
          ))}
        </div>
      )}
      {recut && manualDetail && (
        <div className="mb-3">
          <button className="text-xs font-bold text-slate-500 mb-3 flex items-center" onClick={() => setManualDetail(null)} data-testid="manual-detail-back">
            <Icon id="ic-back" size={12} className="inline-block align-[-1px] mr-1" style={{ color: "currentColor" }} />
            {embedded ? ct("learnearn.manual.back_short", "Back") : ct("settings.back_to", "Back to {tab}", { tab: ct("learnearn.manual.page_title", "{name}'s Learn & Earn", { name: activeChild.name }) })}
          </button>
          <div className="text-lg font-extrabold text-slate-800 leading-tight" data-testid="manual-detail-title">
            {(MANUAL_SUBJECTS.find((x) => x.id === manualDetail.subject) || {}).label} · {(MANUAL_GROUPS.find((g) => g.id === manualDetail.group) || {}).label}
          </div>
        </div>
      )}

      {/* ============================== ENGLISH ============================== */}
      {(!guide || guide.section === "english") && (!recut || (manualDetail && ENGLISH_SUBJECTS.includes(manualDetail.subject))) && (
      <Section id="english" title="English" summary={sectionSummary("english")} guided={!!guide} plain={recut} open={shownSection === "english"} onToggle={() => setOpenSection(openSection === "english" ? null : "english")}>
        {!guide && !recut && <TabBar tabs={[{ id: "spelling", label: "Spelling" }, { id: "vocab", label: "Vocabulary" }, { id: "english", label: "English" }]} active={engTab} onSelect={setEngTab} />}

        {subjectSlot("spelling", shownEngTab === "spelling", (
          <div>
            <GameToggleRow label="Spelling test" childName={activeChild.name} enabled={gameEnabled("spelling")} onToggle={() => toggleGameEnabled("spelling")} />
            {/* R3a — everything here mirrors what ChildView's LearnEarnTab actually serves,
                so the preview below is exactly what the child will see this week. Container
                keeps the spelling-auto-card testid from the old standalone card. */}
            <div data-testid="spelling-auto-card">
              {coachEnabled(state.settings) && (
                (!!spellingAuto.list && spellingAuto.list !== "auto") ? (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2.5 mb-2 flex items-start gap-3" data-testid="adaptive-handback">
                    <p className="text-xs text-slate-600 leading-relaxed m-0 flex-1">{ct("adaptive.handback.body", "You have set this one yourself, so Learning Coach is standing back from spelling.")}</p>
                    <button type="button" className="text-xs font-bold text-slate-900 underline underline-offset-2 flex-shrink-0" data-testid="adaptive-handback-cta" onClick={() => onSpellingListChange("auto")}>{ct("adaptive.handback.cta", "Hand it back")}</button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 leading-relaxed px-1 mb-1" data-testid="adaptive-coach-on-note">
                    {ct("learnearn.coach_runs", "Learning Coach is choosing the words: {n} a test. Change the length from Coach's summary, or take over whenever you like.", { n: coachSizeVal })}
                  </p>
                )
              )}
              {coachEnabled(state.settings) && (() => {
                // "off" is the custom-list key, and it is not a list so it has no label — which
                // is why this line read "Spelling · working in off" in prod. adaptiveSentence
                // falls back to the raw key when the map misses, so the fix belongs here rather
                // than in the engine.
                const labels = { off: ct("learnearn.band.custom", "Custom Words"), ...Object.fromEntries(Object.entries(effectiveSpellingLists()).map(([k, v]) => [k, v.label])), ...Object.fromEntries(Object.entries(effectiveVocabCategories()).map(([k, v]) => [k, v.label])), ...Object.fromEntries(Object.entries(effectiveMathsBands()).map(([k, v]) => [k, v.label])) };
                const lines = ["spelling", "vocab", "mathsworkout", "ttstreaks"].map((g) => adaptiveSentence(g, content.adaptive?.[g], labels)).filter(Boolean);
                if (!lines.length) return null;
                return (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2.5 mb-2" data-testid="adaptive-helping">
                    <div className="text-xs font-bold text-slate-800 mb-1">{ct("adaptive.helping.title", "How Learning Coach is helping {name}", { name: activeChild.name })}</div>
                    {lines.map((l) => <div key={l.game} className="text-xs text-slate-600"><span className="font-semibold text-slate-800">{l.game}</span> · {l.text}</div>)}
                  </div>
                );
              })()}
              <GroupLabel lead={guide ? LEVEL_LEAD : null}>{ct("learnearn.spelling_words_label", "Words")}</GroupLabel>
              <div className="flex flex-col gap-1.5">
                <SettingRow label="Word list" hint="A built-in curriculum list, or your own words">
                  <select data-testid="spelling-list-select" className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs max-w-[55%]" value={spellingAuto.list ?? "auto"} onChange={(e) => onSpellingListChange(e.target.value)}>
                    <option value="auto">{ladderAutoLabel(effectiveSpellingLists()[resolveSpellingList(activeChild, "auto")], effectiveAge(activeChild) ?? "?", coachEnabled(state.settings))}</option>
                    {effectiveSpellingListOrder().map((k) => <option key={k} value={k}>{ladderOptionLabel(effectiveSpellingLists()[k])}</option>)}
                    <option value="off">{ct("learnearn.custom_list_tab", "Custom list")}</option>
                  </select>
                </SettingRow>
                <AdaptiveRow game="spelling" adaptiveOn={coachEnabled(state.settings)} manual={!!spellingAuto.list && spellingAuto.list !== "auto"} state={adaptiveGameState(content.adaptive, "spelling", spellingListKey)} bandLabel={(effectiveSpellingLists()[(content.adaptive?.spelling?.band) || spellingListKey] || {}).label || spellingListKey} topicsInBand={[...new Set(spellingListItems((content.adaptive?.spelling?.band) || spellingListKey).map((w) => w.topic).filter(Boolean))]} onPin={(pinned) => setAdaptivePin("spelling", pinned, spellingListKey)} />
                {spellingIsCustom ? (
                  /* LB-290 (Alan, 15 Sep). The old copy said the words were "below" and they
                     were not — they are on the Custom words page, reached by going back. That
                     one wrong word sent two people hunting for a bug that did not exist.
                     A parent arriving here wants to know their list saved. The count answers
                     that without leaving; the preview lets them recognise their own words; the
                     button names where editing happens instead of leaving them to guess. */
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3" data-testid="spelling-custom-summary">
                    <div className="text-xs font-bold text-slate-800">{ct("learnearn.custom_card.title", "{name}'s own words", { name: activeChild.name })}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{ct("learnearn.custom_card.count", "{n} word{plural} · {ticked} in the next test", { n: content.spellingWords.length, plural: content.spellingWords.length === 1 ? "" : "s", ticked: customTickedCount })}</div>
                    {content.spellingWords.length > 0 && (
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed" data-testid="spelling-custom-preview">
                        {content.spellingWords.slice(0, 5).map((w) => w.word).join(" · ")}
                        {content.spellingWords.length > 5 && <span className="text-slate-400">{ct("learnearn.custom_card.more", " · +{n} more", { n: content.spellingWords.length - 5 })}</span>}
                      </p>
                    )}
                    <button type="button" data-testid="spelling-custom-edit"
                      onClick={() => setManualDetail({ subject: "spelling", group: "custom" })}
                      className="w-full mt-3 bg-slate-900 text-white text-xs font-bold rounded-lg py-2">
                      {content.spellingWords.length ? ct("learnearn.custom_card.edit", "Edit words") : ct("learnearn.custom_card.add", "Add words")}
                    </button>
                  </div>
                ) : coachRunsSpelling ? null : (
                  <>
                    <SettingRow label="Words per week" hint="A fresh batch serves itself every Monday">
                      <Stepper value={spellingBatchVal} min={1} max={SPELLING_AUTO_MAX_BATCH} label="Words per week" onCommit={(n) => setSpellingAuto({ batchSize: n })} />
                    </SettingRow>
                    {spellingWeekBatch.length > 0 && (
                      <WordManager
                        guided={!!guide}
                        label={ct("learnearn.guide.week_words", "This week's words")}
                        hint={ct("learnearn.guide.week_words_hint", "{list} · {n} word{plural}", { list: effectiveSpellingLists()[spellingListKey].label, n: spellingWeekBatch.length, plural: spellingWeekBatch.length === 1 ? "" : "s" })}
                      >
                      <div data-testid="spelling-auto-preview" className="border-t border-slate-200 py-2">
                        <div className="text-xs font-semibold text-teal-700 mb-1">{customTickedCount > 0 ? "Paused — your ticked words are in use. This week's batch would be:" : "This week's words"} · {effectiveSpellingLists()[spellingListKey].label}</div>
                        <div className="text-xs text-teal-900 leading-relaxed">{spellingWeekBatch.map((w) => w.word).join(" · ")}</div>
                      </div>
                      </WordManager>
                    )}
                  </>
                )}
              </div>
            </div>

            {spellingIsCustom && (
              <>
              <GroupLabel>{ct("learnearn.custom_words_heading", "Custom words")}</GroupLabel>
              <WordManager
                guided={!!guide}
                label={ct("learnearn.guide.spelling_words", "Your own spelling words")}
                hint={ct("learnearn.guide.spelling_words_hint", "{n} on the list · {ticked} in the next test", { n: content.spellingWords.length, ticked: customTickedCount })}
              >
              <div className="mt-3">
                {content.spellingWords.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-slate-100">
                      <span className="text-xs font-medium text-slate-400">{customTickedCount} in test</span>
                      <div className="flex items-center gap-3">
                        <button className="text-xs font-bold text-slate-500" onClick={() => setAllWordsTestInclude(true)}>{ct("learnearn.select_all", "All")}</button>
                        <button className="text-xs font-bold text-slate-500" onClick={() => setAllWordsTestInclude(false)}>{ct("learnearn.select_none", "None")}</button>
                        <button className="text-xs font-bold text-teal-600" onClick={() => { setSpellSelectMode((v) => !v); setSelectedWords([]); }}>{spellSelectMode ? "Done" : "Select"}</button>
                      </div>
                    </div>
                    {spellSelectMode && selectedWords.length > 0 && <div className="flex justify-end mb-1.5"><button className="text-xs font-bold text-rose-500" onClick={deleteSelectedWords}>Delete selected ({selectedWords.length})</button></div>}
                    <p className="text-xs text-slate-400 mb-1">{ct("learnearn.words_included_hint", "Words included in the test earn tokens for full marks. Words untick themselves after a reward payout, so re-include some (or new ones) for the next test.")}</p>
                  </>
                )}
                {content.spellingWords.map((w) => {
                  const included = w.testInclude !== false;
                  return (
                    <div key={w.id} className="flex items-center gap-2 py-2 border-b border-slate-50">
                      {spellSelectMode && <input type="checkbox" className="flex-shrink-0" checked={selectedWords.includes(w.id)} onChange={() => toggleWordSelected(w.id)} aria-label={`Select ${w.word} for delete`} />}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${included ? "text-slate-700" : "text-slate-300"}`}>{w.word}</div>
                        <div className={`text-xs font-medium ${included ? "text-slate-400" : "text-slate-300"}`}>{included ? "In the next test" : "Skipped — not in tests"}</div>
                      </div>
                      <button className={`text-xs font-bold flex-shrink-0 ${included ? "text-slate-500" : "text-teal-600"}`} onClick={() => toggleWordTestInclude(w.id)}>{included ? "Skip" : "Include"}</button>
                    </div>
                  );
                })}
                {content.spellingWords.length < SPELLING_CAP && (
                  <div className="flex items-center gap-2 mt-3">
                    <label className="text-xs font-medium text-slate-500 w-24 flex-shrink-0">{ct("learnearn.word_label", "Word")}</label>
                    <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0" value={word} onChange={(e) => setWord(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addWord()} />
                    <button className="bg-slate-900 text-white rounded-lg px-3 py-1.5 text-sm font-bold flex-shrink-0" onClick={addWord}>{ct("learnearn.add_button", "Add")}</button>
                  </div>
                )}
                {/* Point-of-action disclosure (TTS_SCOPE.md §5.3, D10) — one line covers both
                    the single-word row above and the bulk-add area below, since they're the
                    same visible panel and the same destination. Wording is Alan's own. */}
                <p className="text-xs text-slate-400 mt-2">{ct("learnearn.added_to_bank_note", "These words will be added to the Little Bankers word bank.")}</p>
                <div className="flex items-center gap-4 mt-3">
                  <button className="text-xs font-semibold text-slate-800" onClick={() => setShowBulkWords((v) => !v)}>{showBulkWords ? "Cancel bulk add" : "Bulk add words"}</button>
                  {/* LB-363: the photo lands in the bulk box, flagged words listed under it, so the
                      parent still checks every word and taps Add words themselves. */}
                  <input ref={photoInputRef} type="file" accept="image/*" className="hidden" data-testid="custom-photo-file" onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) { setPhotoFile(f); setShowPhoto(true); } }} />
                  <button className="text-xs font-semibold text-slate-800" onClick={() => { if (showPhoto) { setShowPhoto(false); setPhotoFile(null); return; } warmReader(); if (photoInputRef.current) photoInputRef.current.click(); }} data-testid="custom-photo">{showPhoto ? ct("photo.close", "Close the photo") : ct("photo.custom_button", "Photograph the homework")}</button>
                </div>
                {showPhoto && (
                  <div className="mt-2">
                    <PhotoCapture key={photoFile ? `${photoFile.name}-${photoFile.size}-${photoFile.lastModified}` : "pick"} initialFile={photoFile} type="spelling" known={new Set(content.spellingWords.map((w) => String(w.word || "").toLowerCase()))} showToast={showToast}
                      onCancel={() => { setShowPhoto(false); setPhotoFile(null); }}
                      onWords={({ pairs, flags }) => { setBulkWords(pairs.map((p) => p.a).join("\n")); setPhotoFlags(flags.filter((f) => !f.ok)); setShowBulkWords(true); setShowPhoto(false); setPhotoFile(null); }} />
                  </div>
                )}
                {showBulkWords && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-400 mb-1.5">One word per line (or comma-separated). Up to {SPELLING_CAP} total.</p>
                    <textarea className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full h-24" placeholder={"necessary\naccommodation\npersevere"} value={bulkWords} onChange={(e) => setBulkWords(e.target.value)} />
                    {photoFlags.length > 0 && (
                      <div className="rounded-lg px-3 py-2 mt-1.5 text-xs flex flex-col gap-1" style={{ background: "#FEF0DD", color: "#8A5F00" }} data-testid="custom-photo-flags">
                        <span className="font-bold">{ct("photo.flags_title", "{n} to check before adding", { n: photoFlags.length === 1 ? "1 word" : `${photoFlags.length} words` })}</span>
                        {photoFlags.map((f) => (
                          <span key={f.word} className="flex items-center gap-2"><span className="font-bold text-slate-900">{f.word}</span>{f.suggestion && <><span>{ct("homework.create.flag_mean", "Did you mean")}</span><button type="button" className="font-bold text-slate-900 bg-white border border-slate-200 rounded-full px-2.5 py-0.5" onClick={() => { setBulkWords((t) => t.split("\n").map((l) => (l.trim().toLowerCase() === f.word.toLowerCase() ? f.suggestion : l)).join("\n")); setPhotoFlags((fl) => fl.filter((x) => x.word !== f.word)); }}>{f.suggestion}</button><span>?</span></>}</span>
                        ))}
                      </div>
                    )}
                    <button className="w-full mt-2 bg-slate-900 text-white rounded-lg py-1.5 text-sm font-bold" onClick={bulkAddWords}>{ct("learnearn.add_words_button", "Add words")}</button>
                  </div>
                )}
              </div>
              </WordManager>
              </>
            )}

            {/* Reward last (Alan, 30 Aug 2026): a parent decides WHAT a game is before WHAT
                it pays. Choosing the level first also makes the reward a considered number
                rather than the first thing on screen. */}
            {/* Spelling pays like its siblings now — a pass mark of its own, x for a pass,
                y for 100% (Alan, 22 Sep). Every line here reads THIS child's settings; the
                old notice hardcoded "8 out of 10" while the test paid at 75%, so the page
                contradicted itself on Felix's iPad. */}
            <GroupLabel>{ct("learnearn.spelling.test_label", "Test")}</GroupLabel>
            <SettingRow label="Pass mark (%)">
              <Stepper value={spellingPassThresholdVal} min={1} max={99} label="Spelling pass mark percent" onCommit={(n) => setLC({ spellingPassThreshold: n })} />
            </SettingRow>
            <GroupLabel>{ct("learnearn.spelling_reward_label", "Reward")}</GroupLabel>
            {content.spellingPassNotice && (
              <button type="button" className="text-left text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-1" onClick={() => setLC({ spellingPassNotice: false })} data-testid="spelling-pass-notice">
                {ct("learnearn.spelling.pass_notice", "Spelling now pays {p} tokens for a pass ({t}%) and {f} for 100%. Change it here if you'd rather.", { p: content.spellingPassReward ?? 3, t: spellingPassThresholdVal, f: spellingRewardVal })}
              </button>
            )}
            <SettingRow label="Tokens for a pass" hint={ct("learnearn.spelling.pass_hint", "First-time answers at or above the pass mark; 0 means full marks only")}>
              <Stepper value={content.spellingPassReward ?? 3} min={0} max={999} label="Spelling tokens for a pass" onCommit={(n) => setLC({ spellingPassReward: n })} />
            </SettingRow>
            <SettingRow label="Tokens for 100%">
              <Stepper value={spellingRewardVal} min={1} max={999} label="Spelling tokens for one hundred percent" onCommit={(n) => setLC({ spellingReward: n })} />
            </SettingRow>
          </div>
        ))}

        {subjectSlot("vocab", shownEngTab === "vocab", (
          <div>
            <GameToggleRow label="Vocabulary test" childName={activeChild.name} enabled={gameEnabled("vocab")} onToggle={() => toggleGameEnabled("vocab")} />

            <GroupLabel lead={guide ? LEVEL_LEAD : null}>{ct("learnearn.vocab_words_label", "Words")}</GroupLabel>
            {/* Which list is active — same "Auto (age X)" pattern as Maths Workout's
                difficulty band. Only one list is ever active; picking a new one here swaps
                it in immediately (see activateVocabSource). */}
            <div className="flex flex-col gap-1.5">
              <SettingRow label="Active word list" hint={resolvedVocabCategory === "custom" ? `${activeChild.name}'s own words` : `${effectiveVocabCategories()[resolvedVocabCategory].label} · ${activeVocabWords.length} word${activeVocabWords.length === 1 ? "" : "s"}`}>
                <select className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs max-w-[55%]" value={content.vocabCategory ?? "auto"} onChange={(e) => { warnManualOverride(ct("learnearn.manual.game_vocab", "Vocabulary"), e.target.value); activateVocabSource(e.target.value); }}>
                  <option value="auto">{coachEnabled(state.settings) ? ct("learnearn.ladder.adaptive_age", "Learning Coach (age {age})", { age: effectiveAge(activeChild) ?? "?" }) : ct("learnearn.ladder.auto_age", "By age (age {age})", { age: effectiveAge(activeChild) ?? "?" })}</option>
                  {effectiveVocabCategoryOrder().map((k) => <option key={k} value={k}>{effectiveVocabCategories()[k].label}</option>)}
                  <option value="custom">{ct("learnearn.vocab_custom_tab", "Custom (your own words)")}</option>
                </select>
              </SettingRow>
              <AdaptiveRow game="vocab" adaptiveOn={coachEnabled(state.settings)} manual={!!content.vocabCategory && content.vocabCategory !== "auto"} state={adaptiveGameState(content.adaptive, "vocab", resolvedVocabCategory)} bandLabel={(effectiveVocabCategories()[(content.adaptive?.vocab?.band) || resolvedVocabCategory] || {}).label || resolvedVocabCategory} topicsInBand={[1, 2, 3, 4, 5].map((g) => `${(content.adaptive?.vocab?.band) || resolvedVocabCategory}:g${g}`)} onPin={(pinned) => setAdaptivePin("vocab", pinned, resolvedVocabCategory)} />
              {/* Checks the starter list's actual content, not activeVocabWords — the latter
                  can legitimately be 0 for a render or two while the auto-load effect above
                  is still catching up (e.g. right after switching child), which used to show
                  this message even though the list genuinely has words coming. */}
              {resolvedVocabCategory !== "custom" && effectiveStarterVocabList(resolvedVocabCategory).length === 0 && (
                <p className="text-xs text-amber-600 px-1">Word list for {effectiveVocabCategories()[resolvedVocabCategory].label} coming soon — choose Custom above to add your own in the meantime.</p>
              )}
            </div>

            <WordManager
              guided={!!guide}
              label={ct("learnearn.guide.vocab_words", "Browse and edit the word list")}
              hint={ct("learnearn.guide.vocab_words_hint", "{n} word{plural}. Nothing to do here unless you want to", { n: activeVocabWords.length, plural: activeVocabWords.length === 1 ? "" : "s" })}
            >
            <div>
            <div className="flex items-center gap-2 mt-2 mb-2">
              <div className="flex-1 flex items-center gap-1.5 border border-slate-200 rounded-lg px-2 py-1.5">
                <span aria-hidden="true" className="text-slate-300 text-xs">{ct("learnearn.search_label", "Search")}</span>
                <input aria-label="Search words" className="flex-1 min-w-0 text-xs outline-none" placeholder={`Search ${activeVocabWords.length} words`} value={vocabSearch} onChange={(e) => setVocabSearch(e.target.value)} />
              </div>
              <button className="text-xs font-bold text-teal-600 flex-shrink-0" onClick={() => { setVocabSelectMode((v) => !v); setSelectedVocab([]); }}>{vocabSelectMode ? "Done" : "Select"}</button>
            </div>

            {/* Alphabet picker filters the list below to words starting with that letter —
                faster to scan than scrolling a 200+ word list. Letters with no words in the
                active list are greyed out. Hidden while a search is typed (search wins). */}
            {!vocabSearch.trim() && (
              <div className="flex flex-wrap gap-1 mb-2">
                <button onClick={() => setVocabLetterFilter(null)} className={`text-xs font-bold rounded-lg px-2 py-1 ${vocabLetterFilter === null ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"}`}>{ct("learnearn.vocab_select_all", "All")}</button>
                {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map((letter) => {
                  const has = vocabAvailableLetters.has(letter);
                  const active = vocabLetterFilter === letter;
                  return (
                    <button key={letter} disabled={!has} onClick={() => setVocabLetterFilter(letter)}
                      className={`text-xs font-bold rounded-lg w-6 h-6 flex items-center justify-center ${active ? "bg-slate-800 text-white" : has ? "bg-slate-100 text-slate-600" : "bg-slate-50 text-slate-300"}`}>
                      {letter}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Same summary bar pattern as the spelling list: live count + quick All/None
                actions, scoped to the currently active list only. */}
            <div className="flex items-center justify-between pb-1.5 mb-1 border-b border-slate-100">
              <span className="text-xs font-medium text-slate-400">{activeVocabWords.filter((v) => v.testInclude !== false).length} in test</span>
              <div className="flex items-center gap-3">
                <button className="text-xs font-bold text-slate-500" onClick={() => setAllVocabTestInclude(true)}>{ct("learnearn.vocab_all", "All")}</button>
                <button className="text-xs font-bold text-slate-500" onClick={() => setAllVocabTestInclude(false)}>{ct("learnearn.vocab_none", "None")}</button>
                {vocabSelectMode && selectedVocab.length > 0 && <button className="text-xs font-bold text-rose-500" onClick={deleteSelectedVocab}>Delete ({selectedVocab.length})</button>}
              </div>
            </div>
            <ActivityList items={vocabVisibleWords} pageSize={10} emptyText={vocabSearch.trim() ? `No words matching "${vocabSearch.trim()}".` : vocabLetterFilter ? `No words starting with "${vocabLetterFilter}".` : "No words in this list yet."} renderRow={renderVocabRow} />

            {resolvedVocabCategory === "custom" && (
              <>
                <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("learnearn.vocab_word_label", "Word")}</label>
                    <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0" value={vWord} onChange={(e) => setVWord(e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("learnearn.vocab_correct_label", "Correct definition")}</label>
                    <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0" value={vDef} onChange={(e) => setVDef(e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("learnearn.vocab_wrong1_label", "Wrong option 1")}</label>
                    <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0" value={vD1} onChange={(e) => setVD1(e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("learnearn.vocab_wrong2_label", "Wrong option 2")}</label>
                    <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0" value={vD2} onChange={(e) => setVD2(e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-slate-500 w-32 flex-shrink-0">{ct("learnearn.vocab_wrong3_label", "Wrong option 3")}</label>
                    <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-0" value={vD3} onChange={(e) => setVD3(e.target.value)} />
                  </div>
                  <button className="bg-slate-800 text-white rounded-lg py-1.5 text-sm font-bold" onClick={addVocab}>{ct("learnearn.vocab_add_word", "Add word")}</button>
                </div>
                <p className="text-xs text-slate-400 mt-2">{ct("learnearn.vocab_added_to_bank_note", "These words will be added to the Little Bankers word bank.")}</p>
                <button className="text-xs font-semibold text-slate-800 mt-3" onClick={() => setShowBulkVocab((v) => !v)}>{showBulkVocab ? "Cancel bulk add" : "Bulk add vocabulary"}</button>
                {showBulkVocab && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-400 mb-1.5">{ct("learnearn.vocab_bulk_hint", "One entry per line, pipe-separated: word | correct definition | wrong 1 | wrong 2 | wrong 3")}</p>
                    <textarea className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-full h-28" placeholder={"Persevere | To continue despite difficulty | To give up easily | To run quickly | To build with wood"} value={bulkVocabText} onChange={(e) => setBulkVocabText(e.target.value)} />
                    <button className="w-full mt-2 bg-slate-900 text-white rounded-lg py-1.5 text-sm font-bold" onClick={bulkAddVocab}>{ct("learnearn.vocab_add_bulk", "Add vocabulary")}</button>
                  </div>
                )}
              </>
            )}
            </div>
            </WordManager>

            {/* What it is, then what it pays (Alan, 30 Aug 2026) — the word list decides the
                test, the test length decides the effort, and the tokens answer "is that
                worth it?". Reading them in that order is the only order that answers. */}
            <GroupLabel>{ct("learnearn.vocab_test_label", "Test")}</GroupLabel>
            <div className="flex flex-col gap-1.5">
              {coachRunsVocab ? (
                <p className="text-xs text-slate-500 leading-relaxed px-1" data-testid="vocab-coach-size-note">{ct("learnearn.coach_runs", "Learning Coach is choosing the words: {n} a test. Change the length from Coach's summary, or take over whenever you like.", { n: coachSizeVal })}</p>
              ) : (
                <SettingRow label="Questions per test">
                  <Stepper value={vocabTestSizeVal} min={1} max={50} label="Questions per test" onCommit={(n) => setLC({ vocabTestSize: n })} />
                </SettingRow>
              )}
              <SettingRow label="Pass mark (%)">
                <Stepper value={vocabPassThresholdVal} min={1} max={99} label="Pass mark percent" onCommit={(n) => setLC({ vocabPassThreshold: n })} />
              </SettingRow>
            </div>
            <GroupLabel>{ct("learnearn.vocab_reward_label", "Reward")}</GroupLabel>
            <div className="flex flex-col gap-1.5">
              <SettingRow label="Tokens for a pass">
                <Stepper value={vocabPassRewardVal} min={1} max={999} label="Tokens for a pass" onCommit={(n) => { setLC({ vocabPassReward: n }); advisePair(n, vocabFullRewardVal); }} />
              </SettingRow>
              <SettingRow label="Tokens for 100%">
                <Stepper value={vocabFullRewardVal} min={1} max={999} label="Tokens for one hundred percent" onCommit={(n) => { setLC({ vocabFullReward: n }); advisePair(vocabPassRewardVal, n); }} />
              </SettingRow>
            </div>
          </div>
        ))}

        {subjectSlot("english", shownEngTab === "english", (
          <div>
            {/* "What English pays" retired 22 Sep — every value it restated now lives on the
                game's own tile, and the copy it carried had drifted from the settings (a
                hardcoded full-marks-only spelling line, a question count the test was not
                using). The weekly limit is the one thing genuinely shared across both
                English games, so it is the one thing left here. */}
            <GroupLabel>{ct("learnearn.english_rewards_title", "English rewards")}</GroupLabel>
            <SettingRow label="Reward payouts per week" hint="Per test type · resets Monday">
              <Stepper value={englishPayoutsVal} min={1} max={7} label="English reward payouts per week" onCommit={(n) => setLC({ learnEarnPayoutsPerWeek: n })} />
            </SettingRow>
            <p className="text-xs text-slate-400 mt-1.5 px-1">Once {activeChild.name} has hit this many rewards for a test this week, further attempts are practice only until the week resets on Monday. Separate from the Maths limit.</p>
          </div>
        ))}
      </Section>
      )}

      {/* ============================== MATHS ============================== */}
      {(!guide || guide.section === "maths") && (!recut || (manualDetail && MATHS_SUBJECTS.includes(manualDetail.subject))) && (
      <Section id="maths" title="Maths" summary={sectionSummary("maths")} guided={!!guide} plain={recut} open={shownSection === "maths"} onToggle={() => setOpenSection(openSection === "maths" ? null : "maths")}>
        {!guide && !recut && <TabBar tabs={[{ id: "ttstreaks", label: "TT Streaks" }, { id: "mathsworkout", label: "Workout" }, { id: "maths", label: "Maths" }]} active={mathsTab} onSelect={setMathsTab} />}

        {subjectSlot("ttstreaks", shownMathsTab === "ttstreaks", (
          <div>
            <GameToggleRow label="TT Streaks" childName={activeChild.name} enabled={gameEnabled("ttstreaks")} onToggle={() => toggleGameEnabled("ttstreaks")} />
            {/* The year chips sit directly above the grid, so the lead names them (Alan's
                copy, extended 30 Aug 2026): a parent told only "select the tables" will tap
                eleven of them one at a time with a one-tap preset an inch above. */}
            <GroupLabel lead={guide ? ct("learnearn.guide.tables_lead", "Select the tables you want {name} to practise — or tap a year to set them all at once.", { name: activeChild.name }) : null}>{ct("learnearn.tables_in_play", "Tables in play")}</GroupLabel>
            {/* Ladder year presets — labels are curriculum shorthand, chips highlight when
                the current set matches a preset exactly. */}
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {TT_YEAR_PRESETS.map((preset) => {
                const cur = content.ttTables && content.ttTables.length > 0 ? content.ttTables : [2, 3, 4, 5, 10];
                const active = cur.length === preset.tables.length && preset.tables.every((t) => cur.includes(t));
                const fallback = preset.id === "y2" ? "Year 2 \u00b7 2, 5, 10" : preset.id === "y3" ? "Year 3 \u00b7 adds 3, 4, 8" : "Year 4 \u00b7 all to 12";
                return (
                  <button key={preset.id} data-testid={`tt-preset-${preset.id}`} onClick={() => applyTtPreset(preset.tables)} className={`text-xs font-semibold rounded px-2.5 py-1 border ${active ? "bg-teal-600 border-teal-600 text-white" : "border-slate-200 text-slate-500"}`}>
                    {ct(`learnearn.tt.preset_${preset.id}`, fallback)}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {Array.from({ length: 11 }, (_, i) => i + 2).map((n) => {
                const active = (content.ttTables && content.ttTables.length > 0 ? content.ttTables : [2, 3, 4, 5, 10]).includes(n);
                return (
                  <button key={n} onClick={() => toggleTtTable(n)} className={`flex items-center justify-center gap-1 text-xs font-semibold rounded-lg px-2 py-1.5 border ${active ? "bg-teal-50 border-teal-300 text-teal-700" : "border-slate-200 text-slate-500"}`}>{n}×</button>
                );
              })}
            </div>
            {(() => { const tables = content.ttTables && content.ttTables.length ? content.ttTables : []; return <AdaptiveRow game="ttstreaks" adaptiveOn={coachEnabled(state.settings)} manual={false} state={adaptiveGameState(content.adaptive, "ttstreaks", null)} bandLabel={ct("adaptive.row.tables_band", "the tables in play")} topicsInBand={tables.map((t) => `table-${t}`)} onPin={null} />; })()}
            <GroupLabel>{ct("learnearn.tt_test_label", "Test")}</GroupLabel>
            <div className="flex flex-col gap-1.5">
              <SettingRow label="Questions per session">
                <Stepper value={ttSessionSizeVal} min={1} max={40} label="Questions per session" testId="tt-session-size" onCommit={commitTtSessionSize} />
              </SettingRow>
              <SettingRow label="Streak length to earn a reward" hint="Auto-adjusts to 80% when session length changes">
                <Stepper value={ttStreakPassCountVal} min={1} max={ttSessionSizeVal} label="Streak length to earn a reward" onCommit={(n) => setLC({ ttStreakPassCount: n })} />
              </SettingRow>
            </div>
            <GroupLabel>{ct("learnearn.tt_reward_label", "Reward")}</GroupLabel>
            <div className="flex flex-col gap-1.5">
              <SettingRow label="Tokens for that streak">
                <Stepper value={ttStreakPassRewardVal} min={1} max={999} label="Tokens for that streak" onCommit={(n) => { setLC({ ttStreakPassReward: n }); advisePair(n, ttStreakFullRewardVal); }} />
              </SettingRow>
              <SettingRow label="Tokens for a perfect run">
                <Stepper value={ttStreakFullRewardVal} min={1} max={999} label="Tokens for a perfect run" onCommit={(n) => { setLC({ ttStreakFullReward: n }); advisePair(ttStreakPassRewardVal, n); }} />
              </SettingRow>
            </div>
          </div>
        ))}

        {subjectSlot("mathsworkout", shownMathsTab === "mathsworkout", (
          <div>
            <GameToggleRow label="Maths Workout" childName={activeChild.name} enabled={gameEnabled("mathsworkout")} onToggle={() => toggleGameEnabled("mathsworkout")} />
            <GroupLabel lead={guide ? LEVEL_LEAD : null}>{ct("learnearn.maths_band_label", "Difficulty band")}</GroupLabel>
            <select data-testid="maths-band-select" className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs w-full mb-1" value={content.mathsBand ?? "auto"} onChange={(e) => { warnManualOverride(ct("learnearn.manual.game_maths", "Maths"), e.target.value); setLC({ mathsBand: e.target.value }); }}>
              <option value="auto">{ladderAutoLabel(effectiveMathsBands()[resolveMathsBand(activeChild, "auto")], effectiveAge(activeChild) ?? "?", coachEnabled(state.settings))}</option>
              {effectiveMathsBandOrder().map((k) => <option key={k} value={k}>{ladderOptionLabel(effectiveMathsBands()[k])}</option>)}
            </select>
            <p className="text-xs text-slate-400">{effectiveMathsBands()[resolveMathsBand(activeChild, content.mathsBand)].blurb}</p>
            {(() => { const byAge = resolveMathsBand(activeChild, "auto"); const band = (content.adaptive?.mathsworkout?.band) || byAge; return <AdaptiveRow game="mathsworkout" adaptiveOn={coachEnabled(state.settings)} manual={!!content.mathsBand && content.mathsBand !== "auto"} state={adaptiveGameState(content.adaptive, "mathsworkout", byAge)} bandLabel={(effectiveMathsBands()[band] || {}).label || band} topicsInBand={mathsBandTypeIds(band)} onPin={(pinned) => setAdaptivePin("mathsworkout", pinned, byAge)} />; })()}
            <GroupLabel>{ct("learnearn.maths_test_label", "Test")}</GroupLabel>
            <div className="flex flex-col gap-1.5">
              <SettingRow label="Questions per test">
                <Stepper value={mathsTestSizeVal} min={1} max={50} label="Maths questions per test" onCommit={(n) => setLC({ mathsTestSize: n })} />
              </SettingRow>
              <SettingRow label="Pass mark (%)">
                <Stepper value={mathsPassThresholdVal} min={1} max={99} label="Maths pass mark percent" onCommit={(n) => setLC({ mathsPassThreshold: n })} />
              </SettingRow>
              <SettingRow label="Seconds per question" hint="0 = no timer">
                <Stepper value={mathsTimerVal} min={0} max={120} label="Seconds per question" onCommit={(n) => setLC({ mathsTimerSeconds: n })} />
              </SettingRow>
              {mathsTimerVal > 0 && <p className="text-xs text-slate-400 px-1">Shown to {activeChild.name} as a countdown — green, then amber at 3s, red at 1s. Running out counts the question wrong.</p>}
            </div>
            <GroupLabel>{ct("learnearn.maths_reward_label", "Reward")}</GroupLabel>
            <div className="flex flex-col gap-1.5">
              <SettingRow label="Tokens for a pass">
                <Stepper value={mathsPassRewardVal} min={1} max={999} label="Maths tokens for a pass" onCommit={(n) => { setLC({ mathsPassReward: n }); advisePair(n, mathsFullRewardVal); }} />
              </SettingRow>
              <SettingRow label="Tokens for 100%">
                <Stepper value={mathsFullRewardVal} min={1} max={999} label="Maths tokens for one hundred percent" onCommit={(n) => { setLC({ mathsFullReward: n }); advisePair(mathsPassRewardVal, n); }} />
              </SettingRow>
            </div>
          </div>
        ))}

        {subjectSlot("maths", shownMathsTab === "maths", (
          <div>
            <GroupLabel>{ct("learnearn.maths_rewards_title", "Maths rewards")}</GroupLabel>
            <SettingRow label="Reward payouts per week" hint="Shared by both games · resets Monday">
              <Stepper value={mathsPayoutsVal} min={1} max={7} label="Maths reward payouts per week" onCommit={(n) => setLC({ mathsPayoutsPerWeek: n })} />
            </SettingRow>
            <p className="text-xs text-slate-400 mt-1.5 px-1">{ct("learnearn.maths_separate_limit", "Separate from the English payout limit.")}</p>
            {/* "What Maths pays" retired with its English twin, 22 Sep — the games' own
                tiles carry their values now. */}
          </div>
        ))}
      </Section>
      )}

      {/* ============================== JUST FOR FUN ============================== */}
      {/* R5 Arcade + Oinks together at last — both fun-only, neither pays tokens (Law 1),
          and neither belongs among the paying games above. The violet accent (Arcade scope
          §2.2's "deliberately distinct from Learn & Earn's amber") marks the whole section. */}
      {!guide && !hideFun && (
      <Section id="fun" title="Just for fun" summary={sectionSummary("fun")} violet open={openSection === "fun"} onToggle={() => setOpenSection(openSection === "fun" ? null : "fun")}>
        {funBody}
      </Section>
      )}
    </div>
  );

}

/* -------------------------------- Settings -------------------------------- */

export const SPELLING_CAP = 20; // Alan, 11 Sep: 15 → 20 (custom list and, with it, the test)

export function GiveOinks({ child, update, showToast, onComplete }) {
  const [n, setN] = useState(10);
  const [why, setWhy] = useState("");
  const [open, setOpen] = useState(false);
  function give() {
    const amount = Math.max(1, Math.min(50, parseInt(n, 10) || 0));
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (!c) return s;
      ensureCosmetics(c);
      c.oinks += amount;
      recordOinkEarn(c, amount);
      // Alan, 10 Sep: a gift from a grown-up gets the same moment a sibling's gift does —
      // shown once, next time the child opens Kid Mode (ChildView OinkGiftReceived).
      c.pendingOinkGift = { from: null, amount, at: new Date().toISOString() };
      s.transactions.unshift({ id: Math.random().toString(36).slice(2, 12), childId: child.id, amount, reason: why.trim() || ct("parent.oinks.gift_reason", "Oinks from your grown-up"), type: "credit", balance: "oinks", kind: "oink_gift", timestamp: new Date().toISOString() });
      return s;
    });
    track("oink_gift", { band: ageBand(child) || undefined, value: amount });
    showToast?.(ct("parent.oinks.gift_toast", "{n} Oinks given to {name}", { n: amount, name: child.name }));
    setOpen(false); setWhy(""); setN(10);
    if (onComplete) onComplete(); // the quick-action pop-up closes with the toast up (Alan, 22 Sep)
  }
  return (
    <div className="mb-2" data-testid="give-oinks">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-slate-500">{ct("parent.oinks.give_hint", "A birthday, or a few Oinks short of something")}</div>
        <button type="button" className="bg-pill text-navy text-xs font-bold rounded-lg px-3 py-1.5" onClick={() => setOpen((v) => !v)}>{ct("parent.oinks.give", "Give Oinks")}</button>
      </div>
      {open && (
        <div className="mt-2 flex items-center gap-2">
          <input type="number" min="1" max="50" value={n} onChange={(e) => setN(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-20" aria-label={ct("parent.oinks.amount", "Oinks (1–50)")} />
          <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder={ct("parent.oinks.reason_placeholder", "Why? (optional)")} className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-0" />
          <button type="button" className="bg-slate-900 text-white text-xs font-bold rounded-lg px-3 py-2" onClick={give} data-testid="give-oinks-confirm">{ct("parent.oinks.give_confirm", "Give")}</button>
        </div>
      )}
    </div>
  );
}
